import React, { useState, useEffect, useRef } from "react";
import { supabase, signIn, signOut as sbSignOut } from "./supabase";
import { T, C, PH, SH, BS, FL, TH, Dim, Bdg, PC, TR, MA, ES } from "./styles";
import BookingPage from "./BookingPage";
import PublicSite from "./PublicSite";

// ─── ERROR BOUNDARY ──────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding: 20, fontFamily: "monospace", background: "#ffe6e6", border: "1px solid #ff9999", margin: 20}}>
          <h2>React Error</h2>
          <pre>{this.state.error?.toString()}</pre>
          <button onClick={() => this.setState({ hasError: false, error: null })}>Retry</button>
        </div>
      );
    }

    return this.props.children;
  }
}

export { ErrorBoundary };

// ─── BREAKPOINTS ─────────────────────────────────────────────────────────────
function useBreakpoint() {
  const [bp, setBp] = useState(() => {
    const w = typeof window !== "undefined" ? window.innerWidth : 1024;
    return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
  });
  useEffect(() => {
    const fn = () => {
      const w = window.innerWidth;
      setBp(w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop");
    };
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

// ─── SEED DATA ───────────────────────────────────────────────────────────────
// (Removed — app now starts empty and loads user data from Supabase/localStorage.)


const CONTRACT_TERMS = `TERMS & CONDITIONS / TÉRMINOS Y CONDICIONES

1. DEPOSIT: A non-refundable 50% deposit secures your date. / Un depósito no reembolsable del 50% reserva su fecha.
2. BALANCE: Remaining balance due 7 days before event. / El saldo se paga 7 días antes del evento.
3. CANCELLATION: Cancellations within 14 days forfeit the full deposit. / Cancelaciones dentro de 14 días pierden el depósito completo.
4. CHANGES: Changes must be requested 10+ days in advance. / Cambios con 10+ días de anticipación.
5. DAMAGE: Client is responsible for damage to rented items. / El cliente es responsable de daños a artículos rentados.
6. PAYMENT: Accepted via Zelle, Cash App, PayPal, Venmo. / Aceptamos Zelle, Cash App, PayPal y Venmo.`;

// ─── STORAGE (safe — never blocks render) ────────────────────────────────────
const KEYS = { c:"baz-clients", p:"baz-projects", i:"baz-inventory", inv:"baz-invoices", exp:"baz-expenses", con:"baz-contracts" };

const DEFAULT_CONTRACT_TERMS = `TERMS & CONDITIONS / TÉRMINOS Y CONDICIONES

1. DEPOSIT / DEPÓSITO
A non-refundable deposit of 50% is required to secure your event date. The remaining balance is due 7 days before the event.
Se requiere un depósito no reembolsable del 50% para reservar la fecha. El saldo restante debe pagarse 7 días antes del evento.

2. CANCELLATION / CANCELACIÓN
Cancellations made within 14 days of the event date will forfeit the full deposit. Cancellations made more than 14 days in advance will receive a credit toward a future booking.
Las cancelaciones dentro de los 14 días previos al evento perderán el depósito completo.

3. CHANGES / CAMBIOS
Any changes to the order must be requested at least 10 days in advance and are subject to availability and price adjustment.
Cualquier cambio debe solicitarse con al menos 10 días de anticipación.

4. DAMAGE / DAÑOS
The client is responsible for any damage to rented or loaned items during the event. Replacement costs will be billed separately.
El cliente es responsable de cualquier daño a los artículos rentados o prestados.

5. SETUP & BREAKDOWN / INSTALACIÓN Y DESMONTAJE
Bazani Creations will arrive at the agreed time for setup. Client must ensure venue access. Additional waiting time may incur fees.
Bazani Creations llegará a la hora acordada. El cliente debe garantizar acceso al lugar.

6. PAYMENT / PAGO
Accepted payment methods: Zelle, Cash App, PayPal, Venmo. No personal checks accepted.
Métodos de pago aceptados: Zelle, Cash App, PayPal, Venmo. No se aceptan cheques personales.

7. FORCE MAJEURE
Bazani Creations is not liable for failure to perform due to causes beyond reasonable control (weather, natural disasters, illness, etc.).
Bazani Creations no será responsable por causas fuera de su control razonable.`;

const CONTRACT_STATUS = {
  draft:  { label:"Draft",   color:"#a78bfa", bg:"#f5f3ff" },
  sent:   { label:"Sent",    color:T.blue, bg:"#eff6ff" },
  signed: { label:"Signed",  color:T.green, bg:"#f0fdf4" },
  void:   { label:"Void",    color:"#f87171", bg:"#fef2f2" },
};

const EXP_CATS = [
  { id:"supplies",  label:"Supplies & Decor",  icon:"🎀", color:"#f472b6", bg:"#fdf2f8", capex:false },
  { id:"equipment", label:"Equipment (CapEx)",  icon:"🔧", color:"#a78bfa", bg:"#f5f3ff", capex:true  },
  { id:"travel",    label:"Vehicle / Travel",   icon:"🚗", color:T.blue, bg:"#eff6ff", capex:false },
  { id:"marketing", label:"Marketing",          icon:"📣", color:"#fb923c", bg:"#fff7ed", capex:false },
  { id:"software",  label:"Software & Tools",   icon:"💻", color:T.green, bg:"#f0fdf4", capex:false },
  { id:"other",     label:"Other",              icon:"📦", color:"#94a3b8", bg:"#f8fafc", capex:false },
];

// ─── BOOKINGS CONSTANTS ───────────────────────────────────────────────────────
const BOOKING_KEY  = "baz-bookings";
const SHOP_KEY     = "baz-shop";
const TAX_RATE     = 0.0; // set to e.g. 0.08 when applicable

const BOOK_STAGES  = [
  { id:"inquiry",   label:"Inquiry",             icon:"✉",  color:"#a78bfa", bg:"#f5f3ff" },
  { id:"active",    label:"Booking In Progress", icon:"🎀", color:"#2A9D8F", bg:"#f0faf9" },
  { id:"completed", label:"Completed",           icon:"🏁", color:T.green,  bg:"#f0fdf4" },
];
// Migration map: old 6-stage ids → new 3-stage ids
const STAGE_MIGRATION = {
  inquiry:"inquiry", consult:"active", proposal:"active",
  contract:"active", setup:"active", teardown:"completed",
};
const BOOK_EVENT_TYPES = ["Birthday","Wedding","Baby Shower","Graduation","Corporate","Anniversary","Quinceañera","Other"];
const USERS = [
  { id:"u1", name:"Luis",         role:"Admin",       color:"#2A9D8F", avatar:"🌸", pin:"", isAdmin:true  },
  { id:"u2", name:"Hazel",        role:"Owner",       color:"#E76F51", avatar:"🎀", pin:"", isAdmin:true  },
  { id:"u3", name:"Team Member",  role:"Team Member", color:T.amber, avatar:"⭐", pin:"", isAdmin:false },
];
// Derive a display name from a Supabase auth user object
function userName(u) {
  return u?.user_metadata?.full_name || u?.user_metadata?.name || u?.email?.split("@")[0] || "User";
}
function userInitial(u) { return userName(u).charAt(0).toUpperCase(); }


// Convert camelCase object keys to snake_case for Supabase
function toSnake(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const result = {};
  for (const [k,v] of Object.entries(obj)) {
    const snake = k.replace(/([A-Z])/g, "_$1").toLowerCase();
    // Serialize arrays/objects to JSON string for storage
    result[snake] = (Array.isArray(v) || (v && typeof v === "object" && !(v instanceof Date))) ? JSON.stringify(v) : v;
  }
  if (result.id && typeof result.id === "number") result.id = String(result.id);
  return result;
}

// Convert snake_case keys back to camelCase when reading from Supabase
function toCamel(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const result = {};
  for (const [k,v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    // Try to parse JSON strings back to arrays/objects
    if (typeof v === "string" && (v.startsWith("[") || v.startsWith("{"))) {
      try { result[camel] = JSON.parse(v); } catch { result[camel] = v; }
    } else {
      result[camel] = v;
    }
  }
  return result;
}


// safeRead: tries Supabase table, falls back to local storage, then fallback value
async function safeRead(key, fallback) {
  // Map storage keys to Supabase table names
  const TABLE_MAP = {
    "baz-clients":   "clients",
    "baz-projects":  "projects",
    "baz-inventory": "inventory",
    "baz-invoices":  "invoices",
    "baz-expenses":  "expenses",
    "baz-bookings":  "bookings",
    "baz-shop":      "packages",
    "baz-contracts": "contracts",
    "baz-users":     "team_users",
  };
  const table = TABLE_MAP[key];
  if (table) {
    try {
      const { data, error } = await supabase.from(table).select("*");
      if (!error && data !== null) return data.map(toCamel);
    } catch(e) { /* fall through to localStorage */ }
  }
  // Fallback: localStorage
  try {
    const local = localStorage.getItem(key);
    if (local) return JSON.parse(local);
  } catch {}
  return fallback;
}

const TABLE_MAP = {
  "baz-clients":   "clients",
  "baz-projects":  "projects",
  "baz-inventory": "inventory",
  "baz-invoices":  "invoices",
  "baz-expenses":  "expenses",
  "baz-bookings":  "bookings",
  "baz-shop":      "packages",
  "baz-contracts": "contracts",
  "baz-users":     "team_users",
};

// Columns that actually exist in each Supabase table.
// toSnake() can produce extra fields (e.g. "events" on clients, "created_at"
// from a camelCase createdAt number) that make the entire upsert fail silently.
// This whitelist strips those before every write.
const TABLE_COLUMNS = {
  clients:    ["id","name","email","phone","lang","notes"],
  bookings:   ["id","name","email","phone","event_type","event_date","venue","notes","stage","tasks","inquiry_details","source","submitted_at","contact_consent","marketing_consent","attribution"],
  invoices:   ["id","invoice_number","client_id","client_name","project_id","status","issue_date","due_date","line_items","subtotal","total","deposit_paid","balance_due","notes","include_contract"],
  projects:   ["id","client_id","title","type","date","venue","theme","status","deposit","total","items"],
  inventory:  ["id","name","category","qty","available","condition","notes"],
  expenses:   ["id","category","item","description","amount","date","receipt","receipt_name"],
  contracts:  ["id","title","client_id","client_name","booking_id","invoice_id","event_type","event_date","venue","total_value","deliverables","terms","status","sent_date","signed_date","signed_by","notes"],
  packages:   ["id","name","tier","price","description","includes","color","popular"],
  team_users: ["id","auth_id","name","role","color","avatar","is_admin"],
};

function safeWrite(key, value) {
  // Always write to localStorage as backup
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  // Write array data to Supabase via upsert (no delete-all)
  const table = TABLE_MAP[key];
  if (!table || !Array.isArray(value)) return;
  (async () => {
    try {
      if (value.length === 0) return; // don't wipe table on empty array
      const allowed = TABLE_COLUMNS[table];
      const rows = value.map(r => {
        const snake = toSnake(r);
        if (!allowed) return snake;
        // Strip any keys not in the schema so Supabase doesn't reject the row
        // Also convert empty strings to null — empty string is invalid for UUID columns
        const filtered = Object.fromEntries(Object.entries(snake).filter(([k]) => allowed.includes(k)));
        return Object.fromEntries(Object.entries(filtered).map(([k, v]) => [k, v === "" ? null : v]));
      });
      const { error } = await supabase.from(table).upsert(rows, { onConflict: "id" });
      if (error) console.warn("Supabase upsert failed:", table, error.message);
    } catch(e) { console.warn("Supabase write failed:", table, e); }
  })();
}

// safeDelete: remove a single record from Supabase by id
function safeDelete(key, id) {
  try {
    // localStorage update handled by the caller (already filters the array)
    const table = TABLE_MAP[key];
    if (!table) return;
    (async () => {
      try {
        await supabase.from(table).delete().eq("id", String(id));
      } catch(e) { console.warn("Supabase delete failed:", table, e); }
    })();
  } catch {}
}


// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const PROJ_STATUS = {
  inquiry:       { label:"Inquiry",     color:"#a78bfa", bg:"#f5f3ff" },
  booked:        { label:"Booked",      color:T.blue, bg:"#eff6ff" },
  "in-progress": { label:"In Progress", color:"#fb923c", bg:"#fff7ed" },
  complete:      { label:"Complete",    color:T.green, bg:"#f0fdf4" },
};
const INV_STATUS = {
  draft:   { label:"Draft",   color:"#a78bfa", bg:"#f5f3ff" },
  sent:    { label:"Sent",    color:T.blue, bg:"#eff6ff" },
  partial: { label:"Partial", color:"#fb923c", bg:"#fff7ed" },
  paid:    { label:"Paid",    color:T.green, bg:"#f0fdf4" },
  void:    { label:"Void",    color:"#f87171", bg:"#fef2f2" },
};
const EVENT_TYPES = ["Quinceañera","Wedding / Boda","Baby Shower","Birthday / Cumpleaños","Backdrop Only"];
const INV_CATS    = ["Globos","Backdrops","Centros","Señalización","Iluminación","Otros"];
const $           = n => `$${Number(n||0).toFixed(2)}`;
// Must return a valid UUID — all Supabase PK columns are uuid type and
// silently reject non-UUID strings (e.g. "1714..."), which caused writes
// to never land and new tasks/bookings to vanish on refresh.
const uid         = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
const newNum      = () => `BAZ-${new Date().getFullYear()}-${Math.floor(Math.random()*9000)+1000}`;

// ─── ICONS ───────────────────────────────────────────────────────────────────
const I = ({d,s=18}) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d}/></svg>;
const IconUsers  = () => <I d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>;
const IconCal    = () => <I d="M3 9h18M3 3h18v18H3zM16 3v6M8 3v6"/>;
const IconBox    = () => <I d="M21 16V8a2 2 0 0 0-1-1.73L13 2.27a2 2 0 0 0-2 0L4 6.27A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.3 7l8.7 5 8.7-5M12 22V12"/>;
const IconDoc    = () => <I d="M14 2H6a2 2 0 0 0-2 2v16l3-2 3 2 3-2 3 2V4a2 2 0 0 0-2-2zM9 13h6M9 9h6M9 17h3"/>;
const IconPlus   = () => <I d="M12 5v14M5 12h14"/>;
const IconX      = () => <I d="M18 6L6 18M6 6l12 12"/>;
const IconGrid   = () => <I d="M3 12h18" s={20}/>;
const IconTrash  = () => <I d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" s={14}/>;
const IconEye    = () => <I d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>;
const IconDollar = () => <I d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>;
const IconClip   = () => <I d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>;
const IconBook   = () => <I d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>;
const IconCheck  = () => <I d="M20 6L9 17l-5-5" s={16}/>;
const IconChev   = () => <I d="M9 18l6-6-6-6" s={16}/>;
const IconTask   = () => <I d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>;
const IconArrow  = () => <I d="M5 12h14M12 5l7 7-7 7" s={15}/>;
const IconShop   = () => <I d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0"/>;
const IconTrend  = () => <I d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6"/>;
const IconPen    = () => <I d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>;

const OS_TAB_PATHS = {
  dash:"/os", crm:"/os/crm", cli:"/os/clients", book:"/os/bookings", settings:"/os/settings",
  con:"/os/crm/contracts", stock:"/os/crm/inventory", bills:"/os/crm/invoices",
  exps:"/os/crm/expenses", fin:"/os/crm/finances", shop:"/os/crm/packages",
};
function tabFromPath(path) {
  return Object.entries(OS_TAB_PATHS).find(([, route]) => route === path)?.[0] || (path.startsWith("/os/crm/") ? "crm" : "dash");
}

function CrmOverview({go, clients, bookings, invoices, contracts}) {
  const cards = [
    ["cli", "Clients", clients.length, "Contacts and event history"],
    ["book", "Bookings", bookings.length, "Inquiry and event pipeline"],
    ["con", "Contracts", contracts.length, "Agreements and signatures"],
    ["bills", "Invoices", invoices.length, "Quotes, payments, and balances"],
  ];
  return <div><PH title="CRM" sub="Your client relationships, from first inquiry through final payment."/><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14}}>{cards.map(([tab,label,count,description]) => <button key={tab} onClick={()=>go(tab)} style={{background:T.white,border:`1px solid ${T.creamD}`,borderRadius:14,padding:20,textAlign:"left",cursor:"pointer",boxShadow:T.shadowSm,color:T.dark}}><div style={{fontSize:11,color:T.mid,textTransform:"uppercase",letterSpacing:.7,fontWeight:700}}>{label}</div><div style={{fontSize:36,fontWeight:700,margin:"9px 0 4px"}}>{count}</div><div style={{fontSize:12,color:T.mid}}>{description}</div><div style={{color:T.teal,fontSize:12,fontWeight:700,marginTop:16}}>Open {label.toLowerCase()} →</div></button>)}</div></div>;
}

function SettingsOverview({currentUser}) {
  return <div><PH title="Settings" sub="Workspace access and public-site links."/><div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:14}}><section style={{background:T.white,borderRadius:14,padding:22,boxShadow:T.shadowSm}}><div style={{fontWeight:700,marginBottom:12}}>Account</div><div style={{fontSize:13,color:T.mid,lineHeight:1.8}}><strong style={{color:T.dark}}>{userName(currentUser)}</strong><br/>{currentUser?.email}<br/><span style={{color:T.green}}>● Authenticated with Supabase</span></div></section><section style={{background:T.white,borderRadius:14,padding:22,boxShadow:T.shadowSm}}><div style={{fontWeight:700,marginBottom:12}}>Website</div><div style={{fontSize:13,color:T.mid,lineHeight:1.8}}>Public site: <a href="https://www.bazanicreations.com" target="_blank" rel="noreferrer" style={{color:T.teal}}>www.bazanicreations.com</a><br/>Inquiry page: <a href="/inquiry" target="_blank" rel="noreferrer" style={{color:T.teal}}>/inquiry</a><br/>Operating system: <strong style={{color:T.dark}}>/os</strong></div></section><section style={{background:T.white,borderRadius:14,padding:22,boxShadow:T.shadowSm}}><div style={{fontWeight:700,marginBottom:12}}>Security boundary</div><p style={{fontSize:13,color:T.mid,lineHeight:1.7,margin:0}}>All <strong style={{color:T.dark}}>/os/*</strong> pages require a signed-in Supabase user. Public website pages do not load the CRM workspace.</p></section></div></div>;
}

// ─── APP ─────────────────────────────────────────────────────────────────────
function CrmApp() {
  const [ready,      setReady]      = useState(false);
  const [toast,      setToast]      = useState("");
  const [tab,        setTab]        = useState(() => tabFromPath(window.location.pathname.replace(/\/+$/, "") || "/os"));
  const [clients,    setClients]    = useState([]);
  const [projects,   setProjects]   = useState([]); // kept for invoice/contract backward-compat reads
  const [inventory,  setInventory]  = useState([]);
  const [invoices,   setInvoices]   = useState([]);
  const [expenses,   setExpenses]   = useState([]);
  const [bookings,   setBookings]   = useState([]);
  const [packages,   setPackages]   = useState([]);
  const [contracts,  setContracts]  = useState([]);
  const [currentUser,setCurrentUser]= useState(null); // null = not logged in (Supabase user object)
  const [resetConfirm, setResetConfirm] = useState(false);
  const [pwRecovery,  setPwRecovery]  = useState(false); // true when Supabase sends PASSWORD_RECOVERY event

  // modals
  const [cModal,   setCModal]   = useState(null);
  const [pModal,   setPModal]   = useState(null);
  const [iModal,   setIModal]   = useState(null);
  const [invModal, setInvModal] = useState(null);
  const [preview,  setPreview]  = useState(null); // stores full invoice object
  const [expModal, setExpModal] = useState(null);
  const [rcptView, setRcptView] = useState(null);
  const [bkModal,  setBkModal]  = useState(null); // null | "new" | booking obj
  const [bkDetail, setBkDetail] = useState(null); // booking obj for detail view
  const [pkgModal, setPkgModal] = useState(null);
  const [conModal, setConModal] = useState(null);
  const [conView,  setConView]  = useState(null);

  // Persist active tab across refreshes
  useEffect(() => { try { sessionStorage.setItem("baz-tab", tab); } catch {} }, [tab]);
  const navigateTab = tabKey => {
    setTab(tabKey);
    const nextPath = OS_TAB_PATHS[tabKey] || "/os/crm";
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
  };
  useEffect(() => {
    const onPopState = () => { setTab(tabFromPath(window.location.pathname.replace(/\/+$/, "") || "/os")); setBkDetail(null); };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!document.getElementById("bazani-fonts")) {
      const link = document.createElement("link");
      link.id   = "bazani-fonts";
      link.rel  = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,600&display=swap";
      document.head.appendChild(link);
    }
    if (!document.getElementById("bazani-global")) {
      const style = document.createElement("style");
      style.id = "bazani-global";
      style.textContent = `
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'DM Sans', system-ui, sans-serif; -webkit-font-smoothing: antialiased; background: #F5EFE8; }
        ::-webkit-scrollbar { display: none; }
        * { scrollbar-width: none; }
        input, select, textarea, button { font-family: inherit; }
        tr:hover { background: #F5EFE8; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Load saved data — app starts empty, then hydrates from storage
  useEffect(() => {
    // One-time migration: strip legacy seed rows that were cached in
    // localStorage before seed data was removed from the app.
    try {
      if (!localStorage.getItem("baz-seed-purged-v1")) {
        const SEED_IDS = new Set([1,2,3,4,5,"1","2","3","4","5","bk1","bk2","pk1","pk2","pk3"]);
        [KEYS.c, KEYS.p, KEYS.i, BOOKING_KEY, SHOP_KEY].forEach(k => {
          try {
            const raw = localStorage.getItem(k);
            if (!raw) return;
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr)) return;
            const cleaned = arr.filter(item => !SEED_IDS.has(item?.id));
            if (cleaned.length !== arr.length) {
              localStorage.setItem(k, JSON.stringify(cleaned));
            }
          } catch {}
        });
        localStorage.setItem("baz-seed-purged-v1", "1");
      }
    } catch {}

    // One-time migration: remap old 6-stage booking stage ids to new 3-stage ids
    try {
      if (!localStorage.getItem("baz-stage-migrated-v1")) {
        const raw = localStorage.getItem(BOOKING_KEY);
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) {
            const migrated = arr.map(b => ({
              ...b,
              stage: STAGE_MIGRATION[b.stage] || b.stage,
              tasks: (b.tasks||[]).map(t => ({...t, stage: STAGE_MIGRATION[t.stage] || t.stage})),
            }));
            localStorage.setItem(BOOKING_KEY, JSON.stringify(migrated));
          }
        }
        localStorage.setItem("baz-stage-migrated-v1", "1");
      }
    } catch {}

    // Listen for sign-in / sign-out / password-recovery events
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setPwRecovery(true);
        setCurrentUser(session?.user || null);
      } else {
        setPwRecovery(false);
        setCurrentUser(session?.user || null);
      }
    });

    // Track realtime channels for cleanup
    const activeChannels = [];

    (async () => {
      // ── Wait for auth session FIRST before reading any data ──────────────
      // This prevents RLS failures when the token needs a silent refresh.
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUser(session?.user || null);
      setReady(true);

      try {
        const [c, p, i, inv, exp, bk, pkg, con] = await Promise.all([
          safeRead(KEYS.c,      []),
          safeRead(KEYS.p,      []),
          safeRead(KEYS.i,      []),
          safeRead(KEYS.inv,    []),
          safeRead(KEYS.exp,    []),
          safeRead(BOOKING_KEY, []),
          safeRead(SHOP_KEY,    []),
          safeRead(KEYS.con,    []),
        ]);
        setClients(c); setProjects(p); setInventory(i);
        setInvoices(inv); setExpenses(exp); setBookings(bk);
        setPackages(pkg); setContracts(con);
      } catch (error) {
        console.error("Error loading data:", error);
      }

      // ── Real-time Supabase subscriptions (sync across users) ──────────────
      const refreshTable = async (table, setter) => {
        try {
          const { data, error } = await supabase.from(table).select("*");
          if (!error && data) setter(data.map(toCamel));
        } catch {}
      };
      const channels = [
        { table:"bookings",  setter: d => { setBookings(d); try { localStorage.setItem(BOOKING_KEY, JSON.stringify(d)); } catch {} } },
        { table:"invoices",  setter: d => { setInvoices(d); try { localStorage.setItem(KEYS.inv, JSON.stringify(d)); } catch {} } },
        { table:"clients",   setter: d => { setClients(d);  try { localStorage.setItem(KEYS.c, JSON.stringify(d)); } catch {} } },
        { table:"projects",  setter: d => { setProjects(d); try { localStorage.setItem(KEYS.p, JSON.stringify(d)); } catch {} } },
        { table:"contracts", setter: d => { setContracts(d);try { localStorage.setItem(KEYS.con, JSON.stringify(d)); } catch {} } },
        { table:"expenses",  setter: d => { setExpenses(d); try { localStorage.setItem(KEYS.exp, JSON.stringify(d)); } catch {} } },
      ];
      channels.forEach(({ table, setter }) => {
        try {
          const ch = supabase.channel(`realtime-${table}`)
            .on("postgres_changes", { event:"*", schema:"public", table }, () => {
              refreshTable(table, setter);
            })
            .subscribe();
          activeChannels.push(ch);
        } catch {}
      });
    })();

    return () => {
      try { authSub.unsubscribe(); } catch {}
      activeChannels.forEach(ch => { try { supabase.removeChannel(ch); } catch {} });
    };
  }, []);

  const flash = msg => { setToast(msg||"Saved ✓"); setTimeout(() => setToast(""), 2000); };

  // Core save — uses functional setState to avoid stale closures
  const save = (key, setter, data) => {
    setter(data);
    safeWrite(key, data);
    flash();
  };

  // clients
  const saveClient   = c  => { save(KEYS.c, setClients,   c.id ? clients.map(x=>x.id===c.id?c:x)    : [...clients,{...c,id:uid(),events:[]}]); setCModal(null); };
  const delClient    = id => { save(KEYS.c, setClients,   clients.filter(x=>x.id!==id));  safeDelete(KEYS.c, id);   setCModal(null); };
  // projects
  const saveProject  = p  => { save(KEYS.p, setProjects,  p.id ? projects.map(x=>x.id===p.id?p:x)   : [...projects,{...p,id:uid()}]);          setPModal(null); };
  const delProject   = id => { save(KEYS.p, setProjects,  projects.filter(x=>x.id!==id)); safeDelete(KEYS.p, id);   setPModal(null); };
  // inventory
  const saveInv      = i  => { save(KEYS.i, setInventory, i.id ? inventory.map(x=>x.id===i.id?i:x)  : [...inventory,{...i,id:uid()}]);         setIModal(null); };
  const delInv       = id => { save(KEYS.i, setInventory, inventory.filter(x=>x.id!==id)); safeDelete(KEYS.i, id);  setIModal(null); };
  // invoices — use setInvoices with functional update to guarantee latest state
  const computeInvoiceTotals = (v) => {
    const total       = Number(v.total || v.subtotal || 0);
    const payments    = Array.isArray(v.payments) ? v.payments : [];
    // amountPaid = sum of logged payments; fall back to depositPaid for legacy invoices
    const amountPaid  = payments.length > 0
      ? payments.reduce((s, p) => s + Number(p.amount || 0), 0)
      : Number(v.depositPaid || 0);
    const balanceDue  = Math.max(0, total - amountPaid);
    let status = v.status;
    if (amountPaid > 0 && balanceDue <= 0) status = "paid";
    else if (amountPaid > 0 && balanceDue > 0 && ["draft","sent"].includes(status)) status = "partial";
    return { total, amountPaid, depositPaid: amountPaid, balanceDue, status, payments };
  };
  const saveInvoice  = v  => {
    const cl      = clients.find(c=>String(c.id)===String(v.clientId));
    const totals  = computeInvoiceTotals(v);
    const withName = {
      ...v,
      ...totals,
      clientName: cl?.name || v.clientName || "",
      id: v.id || uid(),
      ts: v.ts || Date.now(),
    };
    setInvoices(prev => {
      const next = withName.id && prev.find(x=>x.id===withName.id)
        ? prev.map(x=>x.id===withName.id ? withName : x)
        : [...prev, withName];
      safeWrite(KEYS.inv, next);
      return next;
    });
    flash();
    setInvModal(null);
  };
  const logPayment = (invoiceId, payment) => {
    setInvoices(prev => {
      const next = prev.map(v => {
        if (String(v.id) !== String(invoiceId)) return v;
        const payments = [...(Array.isArray(v.payments) ? v.payments : []), { ...payment, id: uid() }];
        const totals   = computeInvoiceTotals({ ...v, payments });
        return { ...v, ...totals };
      });
      safeWrite(KEYS.inv, next);
      return next;
    });
    flash("Payment logged ✓");
  };
  const delInvoice   = id => {
    setInvoices(prev => {
      const next = prev.filter(x=>x.id!==id);
      safeWrite(KEYS.inv, next);
      return next;
    });
    safeDelete(KEYS.inv, id);
    flash();
    setInvModal(null);
  };
  // expenses
  const saveExpense  = e  => {
    setExpenses(prev => {
      const next = e.id && prev.find(x=>x.id===e.id) ? prev.map(x=>x.id===e.id?e:x) : [...prev,{...e,id:uid(),ts:Date.now()}];
      safeWrite(KEYS.exp, next); return next;
    }); flash(); setExpModal(null);
  };
  const delExpense   = id => {
    setExpenses(prev => { const next=prev.filter(x=>x.id!==id); safeWrite(KEYS.exp,next); return next; });
    safeDelete(KEYS.exp, id);
    flash(); setExpModal(null);
  };
  // bookings
  const saveBooking  = b  => {
    if (b._advance) {
      // called from card arrow button — just advance stage
      const stages = BOOK_STAGES.map(s=>s.id);
      const idx    = stages.indexOf(b.stage);
      const nb     = idx < stages.length-1 ? {...b, stage:stages[idx+1], _advance:undefined} : b;
      delete nb._advance;
      const next = bookings.map(x=>x.id===nb.id?nb:x);
      save(BOOKING_KEY, setBookings, next); setBkModal(null);
      return;
    }
    const isNew = !b.id;
    const bWithId = isNew ? {...b, id:uid(), tasks:[], createdAt:Date.now()} : b;
    const next = isNew ? [...bookings, bWithId] : bookings.map(x=>x.id===b.id?b:x);
    save(BOOKING_KEY, setBookings, next); setBkModal(null);
    if (!isNew) setBkDetail(next.find(x=>x.id===b.id)||null);
  };
  const delBooking   = id => { save(BOOKING_KEY, setBookings, bookings.filter(x=>x.id!==id)); safeDelete(BOOKING_KEY, id); setBkModal(null); setBkDetail(null); };
  const advanceStage = id => {
    const stages = BOOK_STAGES.map(s=>s.id);
    const next   = bookings.map(b=>{
      if (b.id!==id) return b;
      const idx = stages.indexOf(b.stage);
      return idx < stages.length-1 ? {...b, stage:stages[idx+1]} : b;
    });
    save(BOOKING_KEY, setBookings, next);
    setBkDetail(next.find(b=>b.id===id)||null);
  };
  const saveTask = (bookingId, task) => {
    const next = bookings.map(b=>{
      if (b.id!==bookingId) return b;
      const tasks = task.id ? b.tasks.map(t=>t.id===task.id?task:t) : [...(b.tasks||[]),{...task,id:uid()}];
      return {...b,tasks};
    });
    save(BOOKING_KEY, setBookings, next);
    setBkDetail(next.find(b=>b.id===bookingId)||null);
  };
  const delTask = (bookingId, taskId) => {
    const next = bookings.map(b=>{
      if (b.id!==bookingId) return b;
      return {...b, tasks:(b.tasks||[]).filter(t=>t.id!==taskId)};
    });
    save(BOOKING_KEY, setBookings, next);
    setBkDetail(next.find(b=>b.id===bookingId)||null);
  };
  // packages
  const savePkg = pkg => { save(SHOP_KEY, setPackages, pkg.id ? packages.map(x=>x.id===pkg.id?pkg:x) : [...packages,{...pkg,id:uid()}]); setPkgModal(null); };
  const delPkg  = id  => { save(SHOP_KEY, setPackages, packages.filter(x=>x.id!==id)); safeDelete(SHOP_KEY, id); setPkgModal(null); };
  // auth
  const logout = () => { sbSignOut().catch(()=>{}); setCurrentUser(null); };
  // contracts
  const saveContract = con => {
    setContracts(prev => {
      const withId = {...con, id: con.id||uid(), createdAt: con.createdAt||Date.now()};
      const next = withId.id && prev.find(x=>x.id===withId.id) ? prev.map(x=>x.id===withId.id?withId:x) : [...prev,withId];
      safeWrite(KEYS.con, next); return next;
    }); flash(); setConModal(null);
  };
  const delContract = id => {
    setContracts(prev => { const next=prev.filter(x=>x.id!==id); safeWrite(KEYS.con,next); return next; });
    safeDelete(KEYS.con, id);
    flash(); setConModal(null);
  };
  const updateContractStatus = (id, status, extra={}) => {
    setContracts(prev => {
      const next = prev.map(x=>x.id===id ? {...x, status, ...extra} : x);
      safeWrite(KEYS.con, next); return next;
    }); flash();
  };

  const doReset = () => {
    // Clear all local state. Note: this only clears localStorage; to wipe
    // Supabase rows, delete them manually in the Supabase dashboard.
    [KEYS.c, KEYS.p, KEYS.i, KEYS.inv, KEYS.exp, KEYS.con, BOOKING_KEY, SHOP_KEY]
      .forEach(k => { try { localStorage.removeItem(k); } catch {} });
    setClients([]); setProjects([]); setInventory([]);
    setInvoices([]); setExpenses([]); setBookings([]);
    setPackages([]); setContracts([]);
    setResetConfirm(false); flash("Cleared ✓");
  };

  const bp = useBreakpoint();
  const isMobile  = bp === "mobile";
  const isTablet  = bp === "tablet";
  const isDesktop = bp === "desktop";

  // All nav items (Events/Projects tab removed)
  const ALL_NAV = [
    {k:"dash",  icon:<IconGrid/>,   lbl:"Dashboard",           short:"Home"},
    {k:"crm",   icon:<IconUsers/>,  lbl:"CRM",                 short:"CRM"},
    {k:"book",  icon:<IconBook/>,   lbl:"Bookings",            short:"Bookings"},
    {k:"con",   icon:<IconPen/>,    lbl:"Contracts",           short:"Contracts"},
    {k:"cli",   icon:<IconUsers/>,  lbl:"Clients / Clientes",  short:"Clients"},
    {k:"stock", icon:<IconBox/>,    lbl:"Inventory",           short:"Inventory"},
    {k:"bills", icon:<IconDoc/>,    lbl:"Invoices / Facturas", short:"Invoices"},
    {k:"exps",  icon:<IconDollar/>, lbl:"Expenses / Gastos",   short:"Expenses"},
    {k:"fin",   icon:<IconTrend/>,  lbl:"Finances",            short:"Finances"},
    {k:"shop",  icon:<IconShop/>,   lbl:"Shop / Packages",     short:"Shop"},
    {k:"settings", icon:<IconTask/>, lbl:"Settings",           short:"Settings"},
  ];
  // Bottom bar: 5 primary tabs + More
  const PRIMARY_TABS = ["dash","book","bills","exps","fin"];
  const MORE_TABS    = ALL_NAV.filter(n=>!PRIMARY_TABS.includes(n.k));
  const [moreOpen, setMoreOpen] = useState(false);

  const paid    = invoices.filter(v=>v.status==="paid").reduce((s,v)=>s+Number(v.total||0),0);
  const pending = invoices.filter(v=>["sent","partial"].includes(v.status)).reduce((s,v)=>s+Number(v.balanceDue||0),0);
  const active  = bookings.filter(b=>b.stage!=="completed").length;
  const low     = inventory.filter(i=>i.available<2).length;

  const CONTENT = (
    <>
      {/* Booking Hub — full-page view, overrides current tab */}
      {bkDetail ? (
        <BookingHub
          booking={bkDetail}
          onAdvance={advanceStage}
          onSaveTask={saveTask}
          onDelTask={delTask}
          onEdit={b=>{setBkDetail(null);setBkModal(b);}}
          onBack={()=>setBkDetail(null)}
          currentUser={currentUser}
          invoices={invoices}
          contracts={contracts}
          onCreateInvoice={inv=>{setInvModal(inv);setBkDetail(null);navigateTab("bills");}}
          onViewInvoice={inv=>{setPreview(inv);}}
          onViewContract={id=>{setConView(id);}}
          onCreateContract={pre=>{setConModal({...pre,"new":true});}}
        />
      ) : (
        <>
          {tab==="dash"  && <Dash  projects={projects} clients={clients} inventory={inventory} invoices={invoices} bookings={bookings} active={active} paid={paid} pending={pending} low={low} go={navigateTab} currentUser={currentUser}/>}
          {tab==="crm"   && <CrmOverview go={navigateTab} clients={clients} bookings={bookings} invoices={invoices} contracts={contracts}/>}
          {tab==="book"  && <BookingsPage bookings={bookings} onNew={()=>setBkModal("new")} onEdit={setBkModal} onDetail={b=>{setBkDetail(b);}}/>}
          {tab==="con"   && <ContractsPage contracts={contracts} clients={clients} bookings={bookings} invoices={invoices} onNew={()=>setConModal("new")} onEdit={setConModal} onView={id=>setConView(id)} onStatus={updateContractStatus}/>}
          {tab==="cli"   && <Clis  clients={clients} projects={projects} bookings={bookings} onEdit={setCModal} onNew={()=>setCModal("new")}/>}
          {tab==="stock" && <Stock inventory={inventory} onEdit={setIModal} onNew={()=>setIModal("new")}/>}
          {tab==="bills" && <Bills invoices={invoices} clients={clients} projects={projects} onEdit={setInvModal} onNew={()=>setInvModal("new")} onView={v=>setPreview(v)}/>}
          {tab==="exps"  && <Exps  expenses={expenses} onEdit={setExpModal} onNew={()=>setExpModal("new")} onViewRcpt={setRcptView}/>}
          {tab==="fin"   && <Finances invoices={invoices} expenses={expenses}/>}
          {tab==="shop"  && <ShopPage packages={packages} onNew={()=>setPkgModal("new")} onEdit={setPkgModal}/>}
          {tab==="settings" && <SettingsOverview currentUser={currentUser}/>}
        </>
      )}
    </>
  );

  const handleSaveAndInvoice = b => {
    saveBooking(b);
    setTimeout(() => {
      setInvModal({
        invoiceNumber: newNum(),
        clientName: b.name,
        clientEmail: b.email,
        eventType: b.eventType,
        eventDate: b.eventDate,
        status: "draft",
        issueDate: new Date().toISOString().slice(0, 10),
        depositPaid: 0, lineItems: [], ts: Date.now(),
        _fromBooking: true,
      });
      navigateTab("bills");
    }, 100);
  };

  const MODALS = (
    <>
      {cModal   !==null && <ClientMod   data={cModal==="new"?null:cModal}     onSave={saveClient}  onDel={delClient}  onX={()=>setCModal(null)}/>}
      {iModal   !==null && <InvMod      data={iModal==="new"?null:iModal}     onSave={saveInv}     onDel={delInv}     onX={()=>setIModal(null)}/>}
      {invModal !==null && <InvoiceBld  data={invModal==="new"?null:invModal} onSave={saveInvoice} onDel={delInvoice} onX={()=>setInvModal(null)} clients={clients} projects={projects} packages={packages} bookings={bookings}/>}
      {preview  !==null && <InvoiceView inv={invoices.find(x=>x.id===preview.id)||preview} clients={clients} projects={projects} onX={()=>setPreview(null)} onLogPayment={logPayment}/>}
      {expModal !==null && <ExpMod      data={expModal==="new"?null:expModal} onSave={saveExpense} onDel={delExpense} onX={()=>setExpModal(null)}/>}
      {rcptView !==null && <RcptViewer  src={rcptView} onX={()=>setRcptView(null)}/>}
      {bkModal  !==null && <BookingMod  data={bkModal==="new"?null:bkModal}   onSave={saveBooking} onDel={delBooking} onX={()=>setBkModal(null)} onSaveAndInvoice={handleSaveAndInvoice}/>}
      {pkgModal !==null && <PkgMod data={pkgModal==="new"?null:pkgModal} onSave={savePkg} onDel={delPkg} onX={()=>setPkgModal(null)}/>}
      {conModal !==null && <ContractMod data={conModal==="new"?null:(conModal?.new?{...conModal,new:undefined}:conModal)} onSave={saveContract} onDel={delContract} onX={()=>setConModal(null)} clients={clients} bookings={bookings} invoices={invoices}/>}
      {conView  !==null && <ContractView contract={contracts.find(c=>c.id===conView)||{}} onX={()=>setConView(null)} onStatus={updateContractStatus}/>}
    </>
  );

  // ── LOGIN GATE ────────────────────────────────────────────────────────────
  if (!ready || !currentUser) {
    return <LoginScreen loading={!ready}/>;
  }

  // ── PASSWORD RECOVERY GATE ────────────────────────────────────────────────
  if (pwRecovery) {
    return <SetNewPasswordScreen onDone={() => setPwRecovery(false)} />;
  }

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) return (
    <div style={{display:"flex",flexDirection:"column",minHeight:"100vh",background:T.cream,fontFamily:T.fontUI,color:T.dark}}>
      {/* Mobile top bar */}
      <div style={{
        background: T.white,
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 50,
        borderBottom: `1px solid ${T.creamD}`,
        boxShadow: T.shadowSm,
      }}>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:T.teal,fontFamily:T.fontDisplay}}>
            Bazani Creations
          </div>
          <div style={{fontSize:9,color:T.light,letterSpacing:.5,textTransform:"uppercase"}}>
            Event Decor CRM
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {toast && (
            <div style={{fontSize:10,color:T.teal,background:T.teal10,padding:"4px 10px",borderRadius:20,fontWeight:600}}>
              ✓ {toast}
            </div>
          )}
          <button
            onClick={logout}
            title={`${userName(currentUser)} — tap to sign out`}
            style={{width:36,height:36,borderRadius:"50%",background:T.teal,border:"none",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:"#fff",cursor:"pointer",flexShrink:0,boxShadow:`0 2px 8px ${T.teal}40`,fontFamily:T.fontUI}}>
            {userInitial(currentUser)}
          </button>
        </div>
      </div>

      {/* Main scrollable content */}
      <main style={{flex:1,padding:"16px 14px",overflowY:"auto",paddingBottom:80}}>
        {CONTENT}
      </main>

      {/* More drawer overlay */}
      {moreOpen && (
        <div style={{position:"fixed",inset:0,zIndex:90,background:"rgba(0,0,0,.45)"}} onClick={() => setMoreOpen(false)}>
          <div style={{position:"absolute",bottom:72,left:0,right:0,background:T.white,borderRadius:"22px 22px 0 0",padding:"8px 14px 24px"}} onClick={e => e.stopPropagation()}>
            <div style={{width:36,height:4,background:T.lighter,borderRadius:2,margin:"8px auto 16px"}}/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6}}>
              {MORE_TABS.map(({k, icon, short}) => (
                <button key={k}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,padding:"10px 4px",borderRadius:14,border:"none",background:tab===k?T.teal10:T.cream,cursor:"pointer",fontFamily:T.fontUI}}
                  onClick={() => { navigateTab(k); setBkDetail(null); setMoreOpen(false); }}>
                  <span style={{color:tab===k?T.teal:T.mid,display:"flex"}}>{icon}</span>
                  <span style={{fontSize:9,color:tab===k?T.teal:T.mid,fontWeight:tab===k?700:500,textAlign:"center"}}>{short}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom tab bar */}
      <nav style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        background: "rgba(255,255,255,.94)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: `0.5px solid ${T.creamD}`,
        display: "flex",
        zIndex: 80,
        paddingBottom: "env(safe-area-inset-bottom,0px)",
        paddingTop: 8,
      }}>
        {ALL_NAV.filter(n => PRIMARY_TABS.includes(n.k)).map(({k, icon, short}) => (
          <button key={k} onClick={() => { navigateTab(k); setBkDetail(null); }}
            style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"2px 2px 6px",border:"none",background:"transparent",cursor:"pointer",fontFamily:T.fontUI}}>
            <div style={{width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:8,background:tab===k?T.teal10:"transparent",transition:"background .15s"}}>
              <span style={{color:tab===k?T.teal:T.lighter,display:"flex",transition:"color .15s"}}>{icon}</span>
            </div>
            <span style={{fontSize:9,color:tab===k?T.teal:T.lighter,fontWeight:tab===k?700:500}}>{short}</span>
          </button>
        ))}
        <button onClick={() => setMoreOpen(o => !o)}
          style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"2px 2px 6px",border:"none",background:"transparent",cursor:"pointer",fontFamily:T.fontUI}}>
          <div style={{width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:8,background:MORE_TABS.some(n=>n.k===tab)?T.teal10:"transparent"}}>
            <span style={{color:MORE_TABS.some(n=>n.k===tab)?T.teal:T.lighter,fontSize:20,lineHeight:1}}>···</span>
          </div>
          <span style={{fontSize:9,color:MORE_TABS.some(n=>n.k===tab)?T.teal:T.lighter,fontWeight:MORE_TABS.some(n=>n.k===tab)?700:500}}>More</span>
        </button>
      </nav>
      {MODALS}
    </div>
  );

  // ── TABLET LAYOUT (icon-only sidebar) ─────────────────────────────────────
  if (isTablet) return (
    <div style={{display:"flex",minHeight:"100vh",fontFamily:T.fontDisplay,background:T.cream,color:T.dark}}>
      <aside style={{width:62,background:T.dark,display:"flex",flexDirection:"column",alignItems:"center",padding:"16px 0 12px",gap:2,flexShrink:0}}>
        <img src={APP_LOGO} alt="Bazani Creations" style={{width:44,height:44,objectFit:"contain",marginBottom:14}}/>
        {ALL_NAV.map(({k,icon})=>(
          <button key={k} title={ALL_NAV.find(n=>n.k===k)?.lbl} style={{width:44,height:44,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:10,border:"none",background:tab===k?"#2A9D8F":"transparent",color:tab===k?"#fff":T.light,cursor:"pointer",marginBottom:2,transition:"all .15s"}} onClick={()=>{ navigateTab(k); setBkDetail(null); }}>
            {icon}
          </button>
        ))}
        <div style={{marginTop:"auto",paddingTop:12,borderTop:`1px solid #2e5a56`,width:"100%",display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          {toast && <div style={{fontSize:9,color:T.green,textAlign:"center"}}>💾</div>}
        </div>
      </aside>
      <main style={{flex:1,padding:"22px 24px",overflowY:"auto"}}>
        {CONTENT}
      </main>
      {MODALS}
    </div>
  );

  // ── DESKTOP LAYOUT (full sidebar) ─────────────────────────────────────────
  return (
    <div style={C.root}>
      <aside style={C.sidebar}>
        <div style={C.logo}>
          <img src={APP_LOGO} alt="Bazani Creations" style={{width:130,height:"auto",objectFit:"contain",margin:"0 auto 4px"}}/>
        </div>
        {ALL_NAV.map(({k,icon,lbl}) => (
          <button key={k}
            style={{
              ...C.nav,
              ...(tab===k ? C.navOn : {}),
              opacity: tab===k ? 1 : .75,
            }}
            onClick={() => { navigateTab(k); setBkDetail(null); }}>
            <span style={{display:"flex"}}>{icon}</span>
            <span>{lbl}</span>
          </button>
        ))}
        <div style={C.sideFoot}>
          <a href="/inquiry" target="_blank" rel="noreferrer" style={{...C.nav,color:"#d9eee9",textDecoration:"none",background:"rgba(43,173,148,.13)",justifyContent:"center"}}>↗ Open public inquiry page</a>
          {/* Current user */}
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:9,border:`1px solid ${T.teal}40`,background:`${T.teal}15`,marginBottom:4}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:T.teal,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0,fontFamily:T.fontUI}}>{userInitial(currentUser)}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:700,color:T.teal,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{userName(currentUser)}</div>
              <div style={{fontSize:9,color:T.mid,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{currentUser.email}</div>
            </div>
            <button onClick={logout} title="Sign out" style={{background:"none",border:"none",cursor:"pointer",color:T.mid,fontSize:12,padding:"2px 4px",borderRadius:4,flexShrink:0}} onMouseEnter={e=>e.target.style.color="#E76F51"} onMouseLeave={e=>e.target.style.color=T.mid}>↩</button>
          </div>
          {toast && <div style={C.toast}>💾 {toast}</div>}
        </div>
      </aside>
      <main style={C.main}>
        {CONTENT}
      </main>
      {MODALS}
    </div>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────
function Dash({projects,clients,inventory,invoices,bookings,active,paid,pending,low,go,currentUser}) {
  const recent     = [...invoices].sort((a,b)=>(b.ts||0)-(a.ts||0)).slice(0,3);
  const activeBooks= (bookings||[]).filter(b=>b.stage!=="completed").length;
  const myTasks    = (bookings||[]).flatMap(b=>(b.tasks||[]).filter(t=>t.assignee===userName(currentUser)&&!t.done).map(t=>({...t,bookingName:b.name,bookingId:b.id})));
  const upcomingBk = [...(bookings||[])].filter(b=>b.eventDate).sort((a,z)=>new Date(a.eventDate)-new Date(z.eventDate)).slice(0,4);

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <h1 style={{margin:0,fontSize:22,fontWeight:700,color:T.dark}}>Dashboard</h1>
          <p style={{margin:"3px 0 0",fontSize:12,color:T.mid}}>Welcome back · Bienvenida, Bazani Creations 🌸</p>
        </div>
        <img src={APP_LOGO} alt="Bazani Creations" style={{height:64,width:"auto",objectFit:"contain"}}/>
      </div>
      <div style={C.statRow}>
        {[
          {lbl:"Active Bookings",   sub:"Pipeline · Reservas",   val:activeBooks,        clr:T.teal,   stripe:`linear-gradient(90deg,${T.teal},${T.tealLight})`, icon:"📋", bg:T.teal10,   tab:"book"},
          {lbl:"Total Clients",     sub:"Clientes Registrados",  val:clients.length,     clr:T.coral,  stripe:`linear-gradient(90deg,${T.coral},#F4A0B5)`,       icon:"👥", bg:T.coral10,  tab:"cli"},
          {lbl:"Revenue Collected", sub:"Ingresos Cobrados",      val:$(paid),        clr:T.purple, stripe:`linear-gradient(90deg,${T.purple},#BB9FDE)`,       icon:"💰", bg:T.purple10, tab:"fin"},
          {lbl:"Outstanding Balance",sub:"Saldo Pendiente",        val:$(pending),     clr:"#E76F51",stripe:`linear-gradient(90deg,#E76F51,#F4A261)`,           icon:"⏳", bg:"#fef3ee",  tab:"fin"},
          {lbl:"My Open Tasks",     sub:userName(currentUser),    val:myTasks.length, clr:T.amber,  stripe:`linear-gradient(90deg,${T.amber},#F4C47C)`,        icon:"✅", bg:T.amber10,  tab:"book"},
        ].map(m => (
          <div key={m.lbl} onClick={() => go(m.tab)}
            style={{...C.card,padding:16,cursor:"pointer",position:"relative",overflow:"hidden"}}
            onMouseEnter={e=>{e.currentTarget.style.boxShadow=T.shadowMd;e.currentTarget.style.transform="translateY(-2px)";}}
            onMouseLeave={e=>{e.currentTarget.style.boxShadow="";e.currentTarget.style.transform="";}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:m.stripe}}/>
            <div style={{width:32,height:32,borderRadius:10,background:m.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,marginBottom:10}}>{m.icon}</div>
            <div style={{fontSize:28,fontWeight:700,color:m.clr,fontFamily:T.fontDisplay,lineHeight:1,letterSpacing:"-.5px"}}>{m.val}</div>
            <div style={{fontSize:13,fontWeight:600,color:T.dark,marginTop:5}}>{m.lbl}</div>
            <div style={{fontSize:11,color:T.light,marginTop:1}}>{m.sub}</div>
          </div>
        ))}
      </div>

      <SH onClick={()=>go("book")}>Upcoming Bookings · Próximas Reservas</SH>
      <div style={C.grid}>
        {upcomingBk.map(b=>{
          const stg=BOOK_STAGES.find(s=>s.id===b.stage)||BOOK_STAGES[0];
          return (
            <div key={b.id} style={{...C.card,padding:14,cursor:"pointer"}} onClick={()=>go("book")}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:stg.bg,color:stg.color}}>{stg.icon} {stg.label}</span>
                <span style={{fontSize:10,color:T.mid}}>{b.eventType}</span>
              </div>
              <div style={{fontWeight:700,fontSize:13,marginBottom:3}}>{b.name}</div>
              <div style={{fontSize:11,color:T.mid}}>📅 {b.eventDate||"—"} · 📍 {b.venue||"—"}</div>
            </div>
          );
        })}
        {!upcomingBk.length && <ES emoji="📋" title="No bookings yet" sub="Add your first booking"/>}
      </div>

      <SH onClick={()=>go("book")}>My Open Tasks · Mis Tareas ({myTasks.length})</SH>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:8}}>
        {myTasks.slice(0,5).map(t=>{
          return (
            <div key={t.id} onClick={()=>go("book")} style={{...C.card,display:"flex",alignItems:"center",gap:10,padding:"9px 14px",cursor:"pointer"}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:"#2A9D8F",flexShrink:0,display:"inline-block"}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.text}</div>
                <div style={{fontSize:10,color:T.mid}}>{t.bookingName}</div>
              </div>
              <span style={{fontSize:10,color:"#2A9D8F",flexShrink:0}}>›</span>
            </div>
          );
        })}
        {!myTasks.length && <Dim>No open tasks — you're all caught up! ✅</Dim>}
      </div>

      <SH onClick={()=>go("bills")}>Recent Invoices · Facturas Recientes</SH>
      {recent.map(v=>{
        const cl=clients.find(c=>String(c.id)===String(v.clientId));
        const cf=INV_STATUS[v.status]||INV_STATUS.draft;
        const collected = v.status==="paid" ? Number(v.total||0) : Number(v.depositPaid||0);
        const owed      = Number(v.balanceDue||0);
        return (
          <div key={v.id} onClick={()=>go("bills")} style={{...C.card,padding:"10px 14px",marginBottom:7,cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: owed>0?6:0}}>
              <div><b style={{color:"#2A9D8F"}}>{v.invoiceNumber}</b><span style={{color:T.mid,fontSize:12,marginLeft:8}}>{cl?.name}</span></div>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <Bdg cfg={cf}>{cf.label}</Bdg>
                <b style={{color:T.green}}>{$(v.total)}</b>
              </div>
            </div>
            {owed>0 && (
              <div style={{display:"flex",gap:12,fontSize:11,paddingTop:5,borderTop:`1px solid #f0ebe2`}}>
                <span style={{color:"#2A9D8F"}}>✓ Collected: <b>{$(collected)}</b></span>
                <span style={{color:"#E76F51"}}>⏳ Owed: <b>{$(owed)}</b></span>
              </div>
            )}
          </div>
        );
      })}
      {!recent.length && <ES emoji="🧾" title="No invoices yet" sub="Create from a booking or start fresh"/>}

      <SH onClick={()=>go("stock")}>Inventory Alerts · Alertas</SH>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {inventory.filter(i=>i.available<2).map(i=>(
          <span key={i.id} onClick={()=>go("stock")} style={{...C.alertChip,cursor:"pointer"}}>⚠️ {i.name.split(" / ")[0]} — {i.available} left</span>
        ))}
        {!inventory.filter(i=>i.available<2).length && <Dim>All stocked ✅</Dim>}
      </div>
    </div>
  );
}

// ─── BOOKINGS PAGE ────────────────────────────────────────────────────────────
const BK = {
  stagePill: {fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:20,display:"inline-flex",alignItems:"center",gap:4},
  taskRow:   {display:"flex",alignItems:"flex-start",gap:8,padding:"7px 10px",borderRadius:8,background:T.cream,marginBottom:5},
};

function BookingsPage({bookings,onNew,onEdit,onDetail}) {
  const [stgF,setStgF] = useState("all");
  const shown = stgF==="all" ? bookings : bookings.filter(b=>b.stage===stgF);
  const sorted= [...shown].sort((a,b)=>new Date(a.eventDate||0)-new Date(b.eventDate||0));

  return (
    <div>
      <PH title="Bookings" sub={`${bookings.length} total bookings`} action={{lbl:"+ New Booking",fn:onNew}}/>

      {/* Pipeline summary — 3 stage columns */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {BOOK_STAGES.map(s=>{
          const cnt = bookings.filter(b=>b.stage===s.id).length;
          const active = stgF===s.id;
          return (
            <div key={s.id} onClick={()=>setStgF(active?"all":s.id)}
              style={{flex:1,textAlign:"center",padding:"10px 8px",borderRadius:10,background:active?s.bg:"#fff",border:`2px solid ${active?s.color:s.color+"30"}`,cursor:"pointer",transition:"all .15s"}}>
              <div style={{fontSize:18,marginBottom:2}}>{s.icon}</div>
              <div style={{fontSize:18,fontWeight:700,color:s.color,lineHeight:1}}>{cnt}</div>
              <div style={{fontSize:9,color:s.color,opacity:.85,marginTop:2,lineHeight:1.2}}>{s.label}</div>
            </div>
          );
        })}
      </div>
      {stgF!=="all" && (
        <button onClick={()=>setStgF("all")} style={{fontSize:11,color:T.mid,background:"none",border:"none",cursor:"pointer",marginBottom:10,padding:0}}>
          ← Show all bookings
        </button>
      )}

      {/* Booking cards */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {sorted.map(b=>{
          const stg      = BOOK_STAGES.find(s=>s.id===b.stage)||BOOK_STAGES[0];
          const stgIdx   = BOOK_STAGES.findIndex(s=>s.id===b.stage);
          const openTasks= (b.tasks||[]).filter(t=>!t.done).length;
          const doneTasks= (b.tasks||[]).filter(t=>t.done).length;
          const next     = BOOK_STAGES[stgIdx+1];
          return (
            <div key={b.id} style={{...C.card,padding:"14px 16px",cursor:"pointer"}} onClick={()=>onDetail(b)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15,marginBottom:2}}>{b.name}</div>
                  <div style={{fontSize:12,color:T.mid}}>{b.eventType} · 📅 {b.eventDate||"—"} · 📍 {b.venue||"—"}</div>
                </div>
                <span style={{...BK.stagePill,background:stg.bg,color:stg.color}}>{stg.icon} {stg.label}</span>
              </div>

              {/* Progress bar */}
              <div style={{height:4,borderRadius:2,background:"#e8e0d4",marginBottom:10,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${((stgIdx+1)/BOOK_STAGES.length)*100}%`,background:stg.color,borderRadius:2,transition:"width .4s"}}/>
              </div>

              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",gap:12,fontSize:11,color:T.mid}}>
                  <span>✅ {doneTasks} done</span>
                  <span style={{color:openTasks>0?"#E76F51":T.mid}}>⏳ {openTasks} open</span>
                  <span>{b.email}</span>
                </div>
                <div style={{display:"flex",gap:6}} onClick={e=>e.stopPropagation()}>
                  <button style={{...C.editBtn,fontSize:11}} onClick={()=>onDetail(b)}>
                    Open →
                  </button>
                  {next && (
                    <button style={{...C.tealBtn,fontSize:11,padding:"4px 10px",gap:4}} onClick={()=>onEdit({...b,_advance:true})}>
                      <IconArrow/> {next.label}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!sorted.length && <Dim>No bookings{stgF!=="all"?" in this stage — tap a column above to clear filter":""}</Dim>}
      </div>
    </div>
  );
}

// ─── BOOKING HUB (full-page detail view) ─────────────────────────────────────
function BookingHub({booking,onAdvance,onSaveTask,onDelTask,onEdit,onBack,currentUser,invoices,contracts,onCreateInvoice,onViewInvoice,onViewContract,onCreateContract}) {
  const [newTask,  setNewTask]  = useState("");
  const [assignee, setAssignee] = useState(userName(currentUser));
  const [hubTab,   setHubTab]   = useState("tasks");

  const stg    = BOOK_STAGES.find(s=>s.id===booking.stage)||BOOK_STAGES[0];
  const stgIdx = BOOK_STAGES.findIndex(s=>s.id===booking.stage);
  const next   = BOOK_STAGES[stgIdx+1];

  const linkedInvoice  = (invoices||[]).find(v=>v.bookingId===booking.id);
  const linkedContract = (contracts||[]).find(c=>c.bookingId===booking.id);

  const addTask = () => {
    if (!newTask.trim()) return;
    onSaveTask(booking.id,{text:newTask.trim(),assignee,done:false});
    setNewTask("");
  };
  const toggleTask = t => onSaveTask(booking.id,{...t,done:!t.done});

  const openTasks = (booking.tasks||[]).filter(t=>!t.done);
  const doneTasks = (booking.tasks||[]).filter(t=>t.done);

  return (
    <div style={{fontFamily:T.fontUI,color:T.dark}}>
      {/* ── Top bar ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:5,fontSize:13,color:T.mid,padding:0,fontFamily:T.fontUI}}>
          ← Bookings
        </button>
        <div style={{display:"flex",gap:8}}>
          <button style={{...C.editBtn,fontSize:12}} onClick={()=>onEdit(booking)}>Edit Booking</button>
        </div>
      </div>

      {/* ── Event info card ── */}
      <div style={{...C.card,padding:"16px 18px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontWeight:700,fontSize:20,marginBottom:4,fontFamily:T.fontDisplay}}>{booking.name}</div>
            <div style={{fontSize:13,color:T.mid,marginBottom:8}}>{booking.eventType} · 📅 {booking.eventDate||"No date"} · 📍 {booking.venue||"No venue"}</div>
            {booking.email && <div style={{fontSize:12,color:T.mid}}>✉ {booking.email}{booking.phone?` · 📞 ${booking.phone}`:""}</div>}
            {booking.notes && <div style={{fontSize:12,color:T.mid,marginTop:4,fontStyle:"italic"}}>"{booking.notes}"</div>}
          </div>
          <span style={{...BK.stagePill,background:stg.bg,color:stg.color,fontSize:12,padding:"4px 12px"}}>{stg.icon} {stg.label}</span>
        </div>

        {/* Stage stepper */}
        <div style={{display:"flex",alignItems:"center",gap:0,marginTop:14,paddingTop:12,borderTop:`1px solid ${T.creamD}`}}>
          {BOOK_STAGES.map((s,i)=>{
            const done = i < stgIdx;
            const curr = i === stgIdx;
            return (
              <React.Fragment key={s.id}>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",opacity:done||curr?1:.4}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:done?"#2A9D8F":curr?s.bg:"#e8e0d4",border:`2px solid ${done||curr?s.color:"#e8e0d4"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>
                    {done?"✓":s.icon}
                  </div>
                  <div style={{fontSize:9,color:curr?s.color:T.mid,fontWeight:curr?700:400,marginTop:3,textAlign:"center",whiteSpace:"nowrap"}}>{s.label}</div>
                </div>
                {i<BOOK_STAGES.length-1&&<div style={{flex:1,height:2,background:stgIdx>i?"#2A9D8F":"#e8e0d4",margin:"0 4px",marginBottom:14}}/>}
              </React.Fragment>
            );
          })}
        </div>
        {next && (
          <button style={{...C.tealBtn,marginTop:12,width:"100%",justifyContent:"center",gap:6}} onClick={()=>onAdvance(booking.id)}>
            <IconArrow/> Move to {next.label}
          </button>
        )}
        {!next && (
          <div style={{marginTop:12,textAlign:"center",fontSize:12,fontWeight:700,color:T.green,padding:"8px",background:"#f0fdf4",borderRadius:8}}>
            ✅ Booking Complete
          </div>
        )}
      </div>

      {/* ── Hub tabs ── */}
      <div style={{display:"flex",gap:0,borderBottom:`2px solid ${T.creamD}`,marginBottom:16}}>
        {[
          {id:"tasks",    label:`Tasks (${(booking.tasks||[]).length})`},
          {id:"invoice",  label:linkedInvoice?"Invoice ✓":"Invoice"},
          {id:"contract", label:linkedContract?"Contract ✓":"Contract"},
          {id:"details",  label:"Details"},
        ].map(t=>(
          <button key={t.id} onClick={()=>setHubTab(t.id)}
            style={{background:"none",border:"none",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:hubTab===t.id?700:400,color:hubTab===t.id?"#2A9D8F":T.mid,borderBottom:hubTab===t.id?"2px solid #2A9D8F":"2px solid transparent",marginBottom:-2,whiteSpace:"nowrap"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tasks tab ── */}
      {hubTab==="tasks" && (
        <div>
          {/* Add task */}
          <div style={{background:"#f0faf9",borderRadius:10,padding:"12px 14px",marginBottom:16,border:`1px solid #c7eae7`}}>
            <div style={{fontSize:11,fontWeight:700,color:"#2A9D8F",marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Add Task</div>
            <input style={{...C.inp,marginBottom:7}} placeholder="e.g. Finish cutting the display, Buy chair covers…"
              value={newTask} onChange={e=>setNewTask(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&addTask()}/>
            <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,color:T.mid,marginBottom:3}}>Assign to</div>
                <input style={C.inp} value={assignee} onChange={e=>setAssignee(e.target.value)} placeholder="Name…"/>
              </div>
              <button style={{...C.tealBtn,padding:"8px 18px",flexShrink:0}} onClick={addTask}>
                <IconPlus/> Add
              </button>
            </div>
          </div>

          {/* Open tasks */}
          {openTasks.length===0 && doneTasks.length===0 && <Dim>No tasks yet — add one above</Dim>}
          {openTasks.length>0 && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Open · {openTasks.length}</div>
              {openTasks.map(t=>(
                <div key={t.id} style={{...BK.taskRow,background:"#fff",border:`1px solid ${T.creamD}`}}>
                  <button onClick={()=>toggleTask(t)} style={{border:`2px solid #d8d0c4`,borderRadius:"50%",width:20,height:20,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",padding:0,background:"transparent"}}>
                  </button>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600}}>{t.text}</div>
                    <div style={{fontSize:11,color:T.mid,marginTop:1}}>👤 {t.assignee}</div>
                  </div>
                  <button style={{background:"none",border:"none",cursor:"pointer",color:"#f87171",padding:2,display:"flex",flexShrink:0}} onClick={()=>onDelTask(booking.id,t.id)}>
                    <IconTrash/>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Done tasks */}
          {doneTasks.length>0 && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Done · {doneTasks.length}</div>
              {doneTasks.map(t=>(
                <div key={t.id} style={{...BK.taskRow,opacity:.6}}>
                  <button onClick={()=>toggleTask(t)} style={{border:`2px solid #2A9D8F`,borderRadius:"50%",width:20,height:20,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",padding:0,background:"#2A9D8F"}}>
                    <span style={{color:"#fff",fontSize:10,lineHeight:1}}>✓</span>
                  </button>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:600,textDecoration:"line-through",color:T.light}}>{t.text}</div>
                    <div style={{fontSize:10,color:T.mid,marginTop:1}}>👤 {t.assignee}</div>
                  </div>
                  <button style={{background:"none",border:"none",cursor:"pointer",color:"#f87171",padding:2,display:"flex",flexShrink:0}} onClick={()=>onDelTask(booking.id,t.id)}>
                    <IconTrash/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Invoice tab ── */}
      {hubTab==="invoice" && (
        <div>
          {linkedInvoice ? (
            <div style={{...C.card,padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontWeight:700,fontSize:16,color:"#2A9D8F"}}>{linkedInvoice.invoiceNumber}</div>
                  <div style={{fontSize:12,color:T.mid,marginTop:2}}>{linkedInvoice.issueDate||"—"}{linkedInvoice.dueDate?` · Due: ${linkedInvoice.dueDate}`:""}</div>
                </div>
                <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                  background:(INV_STATUS[linkedInvoice.status]||INV_STATUS.draft).bg,
                  color:(INV_STATUS[linkedInvoice.status]||INV_STATUS.draft).color}}>
                  {(INV_STATUS[linkedInvoice.status]||INV_STATUS.draft).label}
                </span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,background:T.cream,borderRadius:8,padding:"10px 12px",marginBottom:14}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Total</div>
                  <div style={{fontWeight:700,fontSize:16}}>{$(linkedInvoice.total)}</div>
                </div>
                <div style={{textAlign:"center",borderLeft:`1px solid #e8e0d4`}}>
                  <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Collected</div>
                  <div style={{fontWeight:600,fontSize:14,color:T.green}}>{$(linkedInvoice.depositPaid)}</div>
                </div>
                <div style={{textAlign:"center",borderLeft:`1px solid #e8e0d4`}}>
                  <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Balance</div>
                  <div style={{fontWeight:700,fontSize:16,color:Number(linkedInvoice.balanceDue)>0?"#E76F51":T.green}}>{$(linkedInvoice.balanceDue)}</div>
                </div>
              </div>
              <button style={{...C.tealBtn,width:"100%",justifyContent:"center"}} onClick={()=>onViewInvoice(linkedInvoice)}>
                <IconEye/> View / Print Invoice
              </button>
            </div>
          ) : (
            <div style={{...C.card,padding:"32px 18px",textAlign:"center"}}>
              <div style={{fontSize:36,marginBottom:10}}>🧾</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>No invoice yet</div>
              <div style={{fontSize:12,color:T.mid,marginBottom:20}}>Create an invoice for this booking to track payments.</div>
              <button style={{...C.tealBtn,justifyContent:"center"}} onClick={()=>onCreateInvoice({
                invoiceNumber:newNum(),
                clientName: booking.name,
                clientEmail: booking.email,
                bookingId: booking.id,
                eventType: booking.eventType,
                eventDate: booking.eventDate,
                status:"draft",
                issueDate:new Date().toISOString().slice(0,10),
                depositPaid:0, lineItems:[], ts:Date.now(),
                _fromBooking: true,
              })}>
                🧾 Create Invoice
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Contract tab ── */}
      {hubTab==="contract" && (
        <div>
          {linkedContract ? (
            <div style={{...C.card,padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15}}>{linkedContract.title||"Contract"}</div>
                  <div style={{fontSize:12,color:T.mid,marginTop:2}}>{linkedContract.eventDate||"—"}</div>
                </div>
                <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                  background:(CONTRACT_STATUS[linkedContract.status]||CONTRACT_STATUS.draft).bg,
                  color:(CONTRACT_STATUS[linkedContract.status]||CONTRACT_STATUS.draft).color}}>
                  {(CONTRACT_STATUS[linkedContract.status]||CONTRACT_STATUS.draft).label}
                </span>
              </div>
              <button style={{...C.tealBtn,width:"100%",justifyContent:"center"}} onClick={()=>onViewContract(linkedContract.id)}>
                <IconEye/> View / Print Contract
              </button>
            </div>
          ) : (
            <div style={{...C.card,padding:"32px 18px",textAlign:"center"}}>
              <div style={{fontSize:36,marginBottom:10}}>📝</div>
              <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>No contract yet</div>
              <div style={{fontSize:12,color:T.mid,marginBottom:20}}>Create a contract for this booking to document the agreement.</div>
              <button style={{...C.tealBtn,justifyContent:"center"}} onClick={()=>onCreateContract({
                bookingId: booking.id,
                clientId: booking.clientId||"",
                title: `${booking.eventType} — ${booking.name}`,
                eventDate: booking.eventDate||"",
                venue: booking.venue||"",
                status: "draft",
              })}>
                📝 Create Contract
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Details tab ── */}
      {hubTab==="details" && (
        <div style={{...C.card,padding:"16px 18px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            {[
              {l:"Name",       v:booking.name},
              {l:"Event Type", v:booking.eventType},
              {l:"Email",      v:booking.email},
              {l:"Phone",      v:booking.phone},
              {l:"Event Date", v:booking.eventDate},
              {l:"Venue",      v:booking.venue},
            ].map(r=>(
              <div key={r.l} style={{background:T.cream,borderRadius:7,padding:"8px 11px"}}>
                <div style={{fontSize:10,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:.4,marginBottom:2}}>{r.l}</div>
                <div style={{fontWeight:600,fontSize:13}}>{r.v||"—"}</div>
              </div>
            ))}
          </div>
          {booking.notes&&(
            <div style={{background:T.cream,borderRadius:7,padding:"8px 11px"}}>
              <div style={{fontSize:10,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:.4,marginBottom:2}}>Notes</div>
              <div style={{fontSize:12,color:"#444",lineHeight:1.5}}>{booking.notes}</div>
            </div>
          )}
          {booking.source === "Website Booking Page" && (
            <div style={{marginTop:12,background:T.teal10,border:`1px solid ${T.teal20}`,borderRadius:7,padding:"10px 11px",fontSize:12,lineHeight:1.55}}>
              <div style={{fontWeight:700,color:T.teal,marginBottom:4}}>Website inquiry · {booking.submittedAt ? new Date(booking.submittedAt).toLocaleString() : "Submitted"}</div>
              <div><b>Service:</b> {booking.inquiryDetails?.service||"—"} · <b>Add-ons:</b> {(booking.inquiryDetails?.addOns||[]).join(", ")||"—"}</div>
              <div><b>Budget:</b> {booking.inquiryDetails?.budget||"—"} · <b>Contact:</b> {booking.inquiryDetails?.contactMethod||"—"} · <b>Marketing:</b> {booking.marketingConsent?"Yes":"No"}</div>
              {booking.attribution?.utm_source && <div><b>UTM:</b> {booking.attribution.utm_source}{booking.attribution.utm_campaign?` / ${booking.attribution.utm_campaign}`:""}</div>}
              <a href="/book" target="_blank" rel="noreferrer" style={{color:T.teal,fontWeight:700}}>Open public booking page ↗</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── BOOKING MODAL (add/edit) ─────────────────────────────────────────────────
function BookingMod({data,onSave,onDel,onX,onSaveAndInvoice}) {
  const [f,sf] = useState(data||{name:"",email:"",phone:"",eventType:"Birthday",eventDate:"",venue:"",notes:"",stage:"inquiry"});
  const [askInvoice, setAskInvoice] = useState(false);
  const s=(k,v)=>sf(x=>({...x,[k]:v}));
  return (
    <MW title={data?"Edit Booking":"New Booking"} onX={onX}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <FL lbl="Client Name"><input style={C.inp} value={f.name} onChange={e=>s("name",e.target.value)} placeholder="Full name"/></FL>
        <FL lbl="Event Type">
          <select style={C.inp} value={f.eventType} onChange={e=>s("eventType",e.target.value)}>
            {BOOK_EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </FL>
        <FL lbl="Email"><input style={C.inp} value={f.email} onChange={e=>s("email",e.target.value)} placeholder="client@email.com"/></FL>
        <FL lbl="Phone"><input style={C.inp} value={f.phone} onChange={e=>s("phone",e.target.value)} placeholder="555-000-0000"/></FL>
        <FL lbl="Event Date"><input type="date" style={C.inp} value={f.eventDate} onChange={e=>s("eventDate",e.target.value)}/></FL>
        <FL lbl="Venue / Location"><input style={C.inp} value={f.venue} onChange={e=>s("venue",e.target.value)} placeholder="Venue name"/></FL>
      </div>
      <FL lbl="Pipeline Stage">
        <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:3}}>
          {BOOK_STAGES.map(stg=>(
            <button key={stg.id} onClick={()=>s("stage",stg.id)}
              style={{padding:"4px 10px",borderRadius:18,border:`2px solid ${f.stage===stg.id?stg.color:"#e0d8d0"}`,background:f.stage===stg.id?stg.bg:"#fff",color:f.stage===stg.id?stg.color:T.mid,cursor:"pointer",fontSize:11,fontWeight:f.stage===stg.id?700:400}}>
              {stg.icon} {stg.label}
            </button>
          ))}
        </div>
      </FL>
      <FL lbl="Notes"><textarea style={{...C.inp,height:60}} value={f.notes} onChange={e=>s("notes",e.target.value)} placeholder="Theme, special requests, how they found you…"/></FL>

      {/* Invoice prompt for new bookings */}
      {!data && (
        <div style={{background:"#fef3e8",borderRadius:8,padding:"10px 12px",marginBottom:4,display:"flex",alignItems:"center",gap:10}}>
          <input type="checkbox" id="create-inv-chk" checked={askInvoice} onChange={e=>setAskInvoice(e.target.checked)}/>
          <label htmlFor="create-inv-chk" style={{fontSize:12,color:"#b8470a",cursor:"pointer",fontWeight:600}}>
            🧾 Create an invoice for this booking after saving
          </label>
        </div>
      )}

      <div style={{display:"flex",gap:7,justifyContent:"flex-end",marginTop:14,borderTop:`1px solid #f0ebe2`,paddingTop:11}}>
        {data && <button style={C.delBtn} onClick={()=>onDel(data.id)}>Delete</button>}
        <button style={C.cancelBtn} onClick={onX}>Cancel</button>
        <button style={C.tealBtn} onClick={()=>{
          if (!data && askInvoice && onSaveAndInvoice) onSaveAndInvoice(f);
          else onSave(f);
        }}>Save · Guardar</button>
      </div>
    </MW>
  );
}

// ─── INVOICES LIST ────────────────────────────────────────────────────────────
function Bills({invoices,clients,projects,onEdit,onNew,onView}) {
  const [f,setF]   = useState("all");
  const isMobile   = window.innerWidth < 640;
  const shown      = f==="all" ? invoices : invoices.filter(v=>v.status===f);
  const totalShown = shown.reduce((s,v)=>s+Number(v.total||0),0);

  return (
    <div>
      <PH title="Invoices · Facturas" sub={`${invoices.length} total`} action={{lbl:"+ New Invoice",fn:onNew}}/>
      <div style={C.filters}>
        {["all",...Object.keys(INV_STATUS)].map(s=>(
          <button key={s} style={{...C.fBtn,...(f===s?C.fOn:{})}} onClick={()=>setF(s)}>
            {s==="all"?"All":INV_STATUS[s].label}
          </button>
        ))}
      </div>

      {/* ── MOBILE: card list ── */}
      {isMobile ? (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {shown.map(v=>{
            const cl = clients.find(c=>String(c.id)===String(v.clientId));
            const pr = projects.find(p=>p.id===v.projectId);
            const cf = INV_STATUS[v.status]||INV_STATUS.draft;
            return (
              <div key={v.id} style={{...C.card,padding:"14px 16px"}}>
                {/* Top row */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:"#2A9D8F"}}>{v.invoiceNumber}</div>
                    <div style={{fontWeight:600,fontSize:13,color:T.dark,marginTop:2}}>{cl?.name||v.clientName||"—"}</div>
                    {pr && <div style={{fontSize:11,color:T.mid,marginTop:1}}>{pr.title}</div>}
                  </div>
                  <Bdg cfg={cf}>{cf.label}</Bdg>
                </div>
                {/* Amounts */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,background:T.cream,borderRadius:8,padding:"8px 10px",marginBottom:10}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Total</div>
                    <div style={{fontWeight:700,fontSize:14,color:T.dark}}>{$(v.total)}</div>
                  </div>
                  <div style={{textAlign:"center",borderLeft:`1px solid #e8e0d4`}}>
                    <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Deposit</div>
                    <div style={{fontWeight:600,fontSize:13,color:T.mid}}>{$(v.depositPaid)}</div>
                  </div>
                  <div style={{textAlign:"center",borderLeft:`1px solid #e8e0d4`}}>
                    <div style={{fontSize:10,color:T.mid,marginBottom:2}}>Balance</div>
                    <div style={{fontWeight:700,fontSize:14,color:Number(v.balanceDue)>0?"#E76F51":T.green}}>{$(v.balanceDue)}</div>
                  </div>
                </div>
                {/* Meta + actions */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:11,color:T.mid}}>{v.dueDate ? `Due: ${v.dueDate}` : "No due date"}</div>
                  <div style={{display:"flex",gap:8}}>
                    <button style={{...C.iconBtn,padding:"7px 10px"}} onClick={()=>onView({...v})}><IconEye/></button>
                    <button style={{...C.editBtn,padding:"6px 14px",fontSize:12}} onClick={()=>onEdit(v)}>Edit</button>
                  </div>
                </div>
              </div>
            );
          })}
          {shown.length>0 && (
            <div style={{...C.card,padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#eef7f6"}}>
              <span style={{fontWeight:700,fontSize:13}}>Total ({shown.length})</span>
              <span style={{fontWeight:800,fontSize:15,color:"#2A9D8F"}}>{$(totalShown)}</span>
            </div>
          )}
          {!shown.length && <ES emoji="🧾" title="No invoices yet" sub="Sin facturas aún"/>}
        </div>
      ) : (
        /* ── DESKTOP: table ── */
        <div style={C.tblWrap}>
          <table style={C.tbl}>
            <thead><tr>{["Invoice #","Client","Event","Due","Total","Deposit","Balance","Status",""].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
            <tbody>
              {shown.map(v=>{
                const cl=clients.find(c=>String(c.id)===String(v.clientId));
                const pr=projects.find(p=>p.id===v.projectId);
                const cf=INV_STATUS[v.status]||INV_STATUS.draft;
                return (
                  <tr key={v.id} style={C.tr}>
                    <td style={{...C.td,fontWeight:700,color:"#2A9D8F"}}>{v.invoiceNumber}</td>
                    <td style={C.td}><b>{cl?.name||v.clientName||"—"}</b></td>
                    <td style={{...C.td,fontSize:12,color:T.mid}}>{pr?.title||"—"}</td>
                    <td style={{...C.td,fontSize:12}}>{v.dueDate||"—"}</td>
                    <td style={{...C.td,fontWeight:700}}>{$(v.total)}</td>
                    <td style={{...C.td,color:T.light}}>{$(v.depositPaid)}</td>
                    <td style={{...C.td,fontWeight:700,color:Number(v.balanceDue)>0?"#E76F51":T.green}}>{$(v.balanceDue)}</td>
                    <td style={C.td}><Bdg cfg={cf}>{cf.label}</Bdg></td>
                    <td style={C.td}>
                      <div style={{display:"flex",gap:5}}>
                        <button style={C.iconBtn} title="Preview" onClick={()=>onView({...v})}><IconEye/></button>
                        <button style={C.editBtn} onClick={()=>onEdit(v)}>Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {shown.length>0&&(
              <tfoot><tr style={{background:"#eef7f6"}}>
                <td colSpan={4} style={{...C.td,fontWeight:700}}>Total ({shown.length})</td>
                <td style={{...C.td,fontWeight:800,color:"#2A9D8F"}}>{$(totalShown)}</td>
                <td colSpan={4}></td>
              </tr></tfoot>
            )}
          </table>
          {!shown.length&&<ES emoji="🧾" title="No invoices yet" sub="Sin facturas aún"/>}
        </div>
      )}
    </div>
  );
}

// ─── INVOICE BUILDER ─────────────────────────────────────────────────────────
function InvoiceBld({data,onSave,onDel,onX,clients,projects,packages,bookings}) {
  const isMobile = window.innerWidth < 640;
  const blank = { invoiceNumber:newNum(), clientId:"", clientName:"", bookingId:"", projectId:"", status:"draft", issueDate:new Date().toISOString().slice(0,10), dueDate:"", depositPaid:0, notes:"", includeContract:true, lineItems:[], ts:Date.now() };
  // If pre-filled from booking (_fromBooking flag), merge booking data
  const initData = data?._fromBooking ? {...blank, ...data} : (data ? {...data} : blank);
  const [form, setForm]   = useState(initData);
  const [catF, setCatF]   = useState("all");
  const [cn,   setCn]     = useState("");
  const [cp,   setCp]     = useState("");
  const [cq,   setCq]     = useState(1);

  // When clientId changes, auto-set clientName
  const upd  = (k,v) => {
    setForm(f => {
      const updated = {...f, [k]:v};
      if (k === "clientId") {
        const cl = clients.find(c => c.id === Number(v) || c.id === v);
        updated.clientName = cl?.name || f.clientName || "";
      }
      return updated;
    });
  };
  const sub  = form.lineItems.reduce((s,li)=>s+(Number(li.price||0)*Number(li.qty||1)),0);
  const tot  = sub;
  const bal  = Math.max(0, tot - Number(form.depositPaid||0));

  // Catalog = packages from Shop, filtered by tier
  const PKG_TIERS = [
    {id:"all",      label:"All"},
    {id:"starter",  label:"Starter",  color:"#E9C46A"},
    {id:"standard", label:"Standard", color:"#2A9D8F"},
    {id:"premium",  label:"Premium",  color:"#E76F51"},
    {id:"custom",   label:"Custom",   color:"#a78bfa"},
  ];
  const catalog = (packages||[]).filter(p => catF==="all" || p.tier===catF);

  const addCat = pkg => {
    if (form.lineItems.find(li=>li.cid===pkg.id)) return;
    setForm(f=>({...f,lineItems:[...f.lineItems,{
      id:uid(), cid:pkg.id, name:pkg.name,
      price:Number(pkg.price||0), qty:1,
      isPackage:true,
      includes:pkg.includes||[],
      color:pkg.color||"#2A9D8F",
      tier:pkg.tier,
    }]}));
  };
  const remCat = id => setForm(f=>({...f,lineItems:f.lineItems.filter(li=>li.id!==id)}));
  const addCus = () => {
    if (!cn||!cp) return;
    setForm(f=>({...f,lineItems:[...f.lineItems,{id:uid(),cid:null,name:cn,es:cn,price:Number(cp),qty:Number(cq)}]}));
    setCn(""); setCp(""); setCq(1);
  };
  const updLI = (id,k,v) => setForm(f=>({...f,lineItems:f.lineItems.map(li=>li.id===id?{...li,[k]:v}:li)}));
  const remLI = id => setForm(f=>({...f,lineItems:f.lineItems.filter(li=>li.id!==id)}));

  return (
    <div style={C.overlay} onClick={onX}>
      <div style={{...C.mbox,maxWidth:isMobile?"100%":860,width:"96%",maxHeight:isMobile?"100vh":"90vh",borderRadius:isMobile?0:13}} onClick={e=>e.stopPropagation()}>
        <div style={C.mhead}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontWeight:700,fontSize:15}}>{data?"Edit Invoice":"New Invoice · Nueva Factura"}</span>
            <code style={{fontSize:11,color:"#a07090"}}>{form.invoiceNumber}</code>
          </div>
          <button style={C.xBtn} onClick={onX}><IconX/></button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",maxHeight:isMobile?"none":"76vh",overflow:isMobile?"visible":"hidden"}}>
          {/* LEFT */}
          <div style={{padding:"16px 18px",overflowY:"auto",borderRight:`1px solid #f0e0ea`}}>
            <BS>Client & Event</BS>
            {/* Pre-fill from booking */}
            {bookings && bookings.length > 0 && (
              <FL lbl="Pre-fill from Booking (optional)">
                <select style={{...C.inp,background:form.bookingId?"#f0faf9":"#fff",borderColor:form.bookingId?"#2A9D8F":"#d8d0c4"}}
                  value={form.bookingId||""}
                  onChange={e=>{
                    const bk = bookings.find(b=>b.id===e.target.value);
                    if (bk) {
                      setForm(f=>({...f,
                        bookingId: bk.id,
                        clientName: bk.name,
                        clientEmail: bk.email,
                        eventType: bk.eventType,
                        eventDate: bk.eventDate,
                      }));
                    } else {
                      setForm(f=>({...f, bookingId:""}));
                    }
                  }}>
                  <option value="">— None —</option>
                  {bookings.map(b=><option key={b.id} value={b.id}>{b.name} · {b.eventType} · {b.eventDate||"no date"}</option>)}
                </select>
              </FL>
            )}
            <FL lbl="Client / Cliente">
              <select style={C.inp} value={form.clientId} onChange={e=>upd("clientId",e.target.value)}>
                <option value="">— Select client —</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </FL>
            <FL lbl="Bill To Name (if not in client list)">
              <input style={C.inp} value={form.clientName||""} onChange={e=>upd("clientName",e.target.value)} placeholder="Name shown on invoice…"/>
            </FL>
            <FL lbl="Linked Event (optional)">
              <select style={C.inp} value={form.projectId} onChange={e=>upd("projectId",e.target.value)}>
                <option value="">— None —</option>
                {projects.filter(p=>!form.clientId||String(p.clientId)===String(form.clientId)).map(p=><option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </FL>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <FL lbl="Issue Date"><input type="date" style={C.inp} value={form.issueDate} onChange={e=>upd("issueDate",e.target.value)}/></FL>
              <FL lbl="Due Date"><input type="date" style={C.inp} value={form.dueDate} onChange={e=>upd("dueDate",e.target.value)}/></FL>
            </div>

            <BS>Payment / Pago</BS>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <FL lbl="Status">
                <select style={C.inp} value={form.status} onChange={e=>upd("status",e.target.value)}>
                  {Object.entries(INV_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
              </FL>
              <FL lbl="Deposit Paid ($)">
                <input type="number" style={C.inp} value={form.depositPaid} onChange={e=>upd("depositPaid",e.target.value)}/>
              </FL>
            </div>

            <BS>Add-on / Custom Line Item</BS>
            <div style={{display:"grid",gridTemplateColumns:"1fr 46px 68px",gap:6,alignItems:"end"}}>
              <FL lbl="Description"><input style={C.inp} value={cn} onChange={e=>setCn(e.target.value)} placeholder="Service name…"/></FL>
              <FL lbl="Qty"><input type="number" style={{...C.inp,textAlign:"center"}} min={1} value={cq} onChange={e=>setCq(e.target.value)}/></FL>
              <FL lbl="Price $"><input type="number" style={C.inp} value={cp} onChange={e=>setCp(e.target.value)} placeholder="0"/></FL>
            </div>
            <button style={{...C.tealBtn,fontSize:12,padding:"6px 13px",marginBottom:12}} onClick={addCus}><IconPlus/> Add Custom</button>

            <FL lbl="Notes for client">
              <textarea style={{...C.inp,height:52}} value={form.notes} onChange={e=>upd("notes",e.target.value)} placeholder="Thank you for choosing Bazani Creations!"/>
            </FL>
            <label style={{display:"flex",alignItems:"center",gap:7,fontSize:12,cursor:"pointer",color:"#6b4060",marginBottom:12}}>
              <input type="checkbox" checked={form.includeContract} onChange={e=>upd("includeContract",e.target.checked)}/>
              Include contract terms
            </label>

            <div style={{background:"#eef7f6",borderRadius:8,padding:"11px 13px"}}>
              <TR l="Subtotal" v={$(sub)}/>
              <TR l="TOTAL" v={$(tot)} bold big/>
              <TR l="Deposit Paid" v={`– ${$(form.depositPaid||0)}`} dim/>
              <TR l="Balance Due" v={$(bal)} bold clr={bal>0?"#fb923c":T.green}/>
            </div>
          </div>

          {/* RIGHT — packages from Shop */}
          <div style={{padding:"16px 18px",overflowY:"auto",display:"flex",flexDirection:"column"}}>
            <BS>Packages</BS>

            {/* Tier filter */}
            <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
              {PKG_TIERS.map(t=>{
                const active = catF===t.id;
                return (
                  <button key={t.id} onClick={()=>setCatF(t.id)} style={{
                    ...C.fBtn, fontSize:10, padding:"3px 9px",
                    ...(active ? {background:t.color||"#2A9D8F",color:"#fff",borderColor:t.color||"#2A9D8F"} : {}),
                  }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Package list */}
            <div style={{border:`1px solid #e8e0d4`,borderRadius:8,overflow:"hidden",flex:1,overflowY:"auto",marginBottom:12,maxHeight:260}}>
              {catalog.length===0 && (
                <div style={{padding:"24px 16px",textAlign:"center"}}>
                  <div style={{fontSize:24,marginBottom:6}}>📦</div>
                  <div style={{fontSize:12,fontWeight:600,color:T.dark,marginBottom:3}}>No packages yet</div>
                  <div style={{fontSize:11,color:T.mid}}>Build your packages in the Shop tab — they'll appear here.</div>
                </div>
              )}
              {catalog.map(pkg=>{
                const li  = form.lineItems.find(x=>x.cid===pkg.id);
                const on  = !!li;
                const clr = pkg.color||"#2A9D8F";
                const tier = PKG_TIERS.find(t=>t.id===pkg.tier);
                return (
                  <div key={pkg.id} style={{padding:"9px 10px",borderBottom:`1px solid #f5f0e8`,background:on?"#f0faf9":"#fff"}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                          <span style={{width:8,height:8,borderRadius:"50%",background:clr,display:"inline-block",flexShrink:0}}/>
                          <span style={{fontSize:12,fontWeight:on?700:600,color:on?clr:T.dark}}>{pkg.name}</span>
                          {tier && tier.id!=="all" && (
                            <span style={{fontSize:9,fontWeight:700,color:tier.color||clr,background:(tier.color||clr)+"22",padding:"1px 5px",borderRadius:8,textTransform:"uppercase"}}>{tier.label}</span>
                          )}
                          {pkg.popular && <span style={{fontSize:9,fontWeight:700,color:"#E76F51",background:"#fff4f1",padding:"1px 5px",borderRadius:8}}>★ Popular</span>}
                        </div>
                        {(pkg.includes||[]).length>0 && (
                          <div style={{marginTop:4,paddingLeft:13}}>
                            {pkg.includes.map((item,i)=>(
                              <div key={i} style={{fontSize:10,color:T.mid,lineHeight:1.5}}>✓ {item}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                        <span style={{fontSize:12,fontWeight:700,color:clr}}>{$(pkg.price)}</span>
                        <button
                          style={{...C.editBtn,padding:"2px 8px",fontSize:11,
                            background:on?"#f0fdf4":"#f0faf9",
                            color:on?T.green:clr,
                            border:`1px solid ${on?"#a7f3d0":"#b0ddd9"}`}}
                          onClick={()=>on?remCat(li.id):addCat(pkg)}>
                          {on?"✓ Added":"+ Add"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Invoice Line Items */}
            <BS>Invoice Items ({form.lineItems.length})</BS>
            {!form.lineItems.length
              ? <Dim>No items yet — pick a package above or add a custom line below</Dim>
              : <div style={{border:`1px solid #e8e0d4`,borderRadius:8,overflow:"hidden",marginBottom:8}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 44px 64px 26px",gap:3,padding:"5px 8px",background:"#eef7f6"}}>
                    {["Package / Item","Qty","Price",""].map(h=><div key={h} style={{fontSize:10,fontWeight:700,color:"#2A9D8F",textTransform:"uppercase"}}>{h}</div>)}
                  </div>
                  {form.lineItems.map((li,idx)=>(
                    <div key={li.id} style={{display:"grid",gridTemplateColumns:"1fr 44px 64px 26px",gap:3,padding:"7px 8px",alignItems:"flex-start",background:idx%2?T.cream:"#fff",borderTop:`1px solid #f0ebe2`}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{width:7,height:7,borderRadius:"50%",background:li.color||"#2A9D8F",display:"inline-block",flexShrink:0}}/>
                          <span style={{fontSize:12,fontWeight:700,color:li.color||T.dark}}>{li.name}</span>
                          {li.isPackage && <span style={{fontSize:9,background:"#eef7f6",color:"#2A9D8F",padding:"1px 5px",borderRadius:8,fontWeight:700}}>PKG</span>}
                        </div>
                        {li.includes && li.includes.length>0 && (
                          <div style={{paddingLeft:12,marginTop:2}}>
                            {li.includes.map((item,i)=>(
                              <div key={i} style={{fontSize:9,color:T.mid,lineHeight:1.4}}>✓ {item}</div>
                            ))}
                          </div>
                        )}
                      </div>
                      <input type="number" min={1} value={li.qty} onChange={e=>updLI(li.id,"qty",Number(e.target.value))} style={{...C.inp,padding:"3px 4px",fontSize:12,textAlign:"center"}}/>
                      <input type="number" value={li.price} onChange={e=>updLI(li.id,"price",Number(e.target.value))} style={{...C.inp,padding:"3px 5px",fontSize:12}}/>
                      <button style={{background:"none",border:"none",cursor:"pointer",color:"#f87171",padding:1,display:"flex",marginTop:2}} onClick={()=>remLI(li.id)}><IconTrash/></button>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",padding:"11px 18px",borderTop:`1px solid ${T.creamD}`}}>
          {data && <button style={C.delBtn} onClick={()=>onDel(data.id)}>Delete</button>}
          <button style={C.cancelBtn} onClick={onX}>Cancel</button>
          <button style={C.tealBtn} onClick={()=>onSave({...form,subtotal:sub,total:tot,balanceDue:bal})}>Save Invoice · Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ─── INVOICE PREVIEW ─────────────────────────────────────────────────────────
function printInvoice(inv, clientName, clientEmail, clientPhone, prTitle, prDate) {
  const fmt = n => "$" + Number(n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",");
  const lineRows = (inv.lineItems||[]).map(li => `
    <tr>
      <td style="padding:10px 12px;border-bottom:1px solid #f0ebe2;">
        <div style="display:flex;align-items:center;gap:6px;">
          ${li.isPackage?`<span style="width:8px;height:8px;border-radius:50%;background:${li.color||"#2A9D8F"};display:inline-block;flex-shrink:0;"></span>`:""}
          <span style="font-weight:700;color:${li.isPackage?"#2A9D8F":"#1a2e2b"}">${li.name}</span>
          ${li.isPackage?`<span style="font-size:9px;background:#eef7f6;color:#2A9D8F;padding:1px 5px;border-radius:6px;font-weight:700;">PKG</span>`:""}
        </div>
        ${li.isPackage&&li.includes&&li.includes.length?li.includes.map(i=>`<div style="font-size:10px;color:#7ab8b4;padding-left:14px;line-height:1.6;">✓ ${i}</div>`).join(""):""}
      </td>
      <td style="padding:10px 12px;text-align:right;color:#666;border-bottom:1px solid #f0ebe2;">${li.qty}</td>
      <td style="padding:10px 12px;text-align:right;color:#666;border-bottom:1px solid #f0ebe2;">${fmt(li.price)}</td>
      <td style="padding:10px 12px;text-align:right;font-weight:700;color:#1a2e2b;border-bottom:1px solid #f0ebe2;">${fmt(Number(li.price||0)*Number(li.qty||1))}</td>
    </tr>`).join("");

  const balClr = Number(inv.balanceDue||0)>0?"#E76F51":"#2A9D8F";
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Invoice ${inv.invoiceNumber} — Bazani Creations</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#1a2e2b;padding:32px 40px;max-width:720px;margin:0 auto;}
  @media print{body{padding:16px 20px;}@page{margin:0.5in;}}
  @media(max-width:600px){body{padding:16px;}}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #eef7f6;padding-bottom:18px;margin-bottom:18px;flex-wrap:wrap;gap:12px;}
  .brand{font-size:22px;font-weight:800;color:#2A9D8F;letter-spacing:.5px;}
  .brand-sub{font-size:11px;color:#7ab8b4;margin-top:2px;}
  .inv-num{font-size:22px;font-weight:800;text-align:right;}
  .inv-meta{font-size:11px;color:#7ab8b4;text-align:right;margin-top:4px;}
  .bill-box{background:#f7f3ec;border-radius:8px;padding:12px 16px;margin-bottom:18px;}
  .bill-label{font-size:10px;font-weight:700;color:#2A9D8F;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px;}
  .bill-name{font-size:16px;font-weight:700;}
  .bill-sub{font-size:12px;color:#7ab8b4;margin-top:3px;}
  table{width:100%;border-collapse:collapse;margin-bottom:4px;}
  th{padding:8px 12px;font-size:10px;font-weight:700;color:#2A9D8F;text-transform:uppercase;background:#eef7f6;}
  th:first-child{text-align:left;}th:not(:first-child){text-align:right;}
  .totals{background:#eef7f6;border-radius:8px;padding:14px 16px;margin:12px 0;}
  .total-row{display:flex;justify-content:space-between;align-items:center;font-size:13px;margin-bottom:6px;}
  .total-row:last-child{margin-bottom:0;}
  .total-big{font-size:16px;font-weight:800;border-top:1px solid #d8f0ed;padding-top:8px;margin-top:4px;}
  .payment{background:#f0fdf4;border-radius:8px;padding:10px 14px;font-size:12px;color:#1a4a2a;margin-bottom:14px;}
  .notes{font-style:italic;color:#555;font-size:12px;background:#f7f3ec;border-radius:7px;padding:10px 14px;margin-bottom:14px;}
  .terms{font-size:9px;color:#999;border-top:1px solid #ede8e0;padding-top:14px;white-space:pre-wrap;line-height:1.7;}
  .no-print{margin-top:24px;text-align:center;}
  .print-btn{background:#2A9D8F;color:#fff;border:none;padding:12px 32px;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer;}
  @media print{.no-print{display:none;}}
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="brand">Bazani Creations</div>
    <div class="brand-sub">Custom Event Decor · Columbus, Ohio</div>
  </div>
  <div>
    <div class="inv-num">${inv.invoiceNumber}</div>
    <div class="inv-meta">
      Issued: ${inv.issueDate||"—"}${inv.dueDate?` &nbsp;·&nbsp; Due: ${inv.dueDate}`:""}
    </div>
  </div>
</div>

<div class="bill-box">
  <div class="bill-label">Bill To / Facturar a</div>
  <div class="bill-name">${clientName}</div>
  ${clientEmail||clientPhone?`<div class="bill-sub">${[clientEmail,clientPhone].filter(Boolean).join(" · ")}</div>`:""}
  ${prTitle?`<div class="bill-sub" style="margin-top:4px;">📅 Event: ${prTitle}${prDate?` · ${prDate}`:""}</div>`:""}
</div>

${(inv.lineItems||[]).length?`
<table>
  <thead><tr>
    <th>Service / Package</th><th>Qty</th><th>Unit Price</th><th>Amount</th>
  </tr></thead>
  <tbody>${lineRows}</tbody>
</table>`:"<p style='text-align:center;color:#aaa;padding:20px 0;font-size:12px;'>No line items</p>"}

<div class="totals">
  <div class="total-row"><span>Subtotal</span><span>${fmt(inv.subtotal||inv.total||0)}</span></div>
  <div class="total-row total-big"><span style="font-weight:800;">TOTAL</span><span>${fmt(inv.total||0)}</span></div>
  <div class="total-row" style="color:#7ab8b4;"><span>Deposit Paid / Depósito</span><span>– ${fmt(inv.depositPaid||0)}</span></div>
  <div class="total-row" style="font-weight:800;font-size:15px;color:${balClr};border-top:1px solid #d8f0ed;padding-top:8px;margin-top:4px;">
    <span>Balance Due / Saldo Pendiente</span><span>${fmt(inv.balanceDue||0)}</span>
  </div>
</div>

${inv.notes?`<div class="notes"><strong>Note:</strong> ${inv.notes}</div>`:""}

<div class="payment"><strong>Payment accepted / Aceptamos:</strong> Zelle · Cash App · PayPal · Venmo</div>

${inv.includeContract?`<pre class="terms">${CONTRACT_TERMS}</pre>`:""}

<div class="no-print">
  <button class="print-btn" onclick="window.print()">📄 Save as PDF / Print</button>
</div>
<script>
  // Auto-open print dialog on desktop; on iPhone the user taps the button
  if(!/iPhone|iPad|iPod/.test(navigator.userAgent)){
    setTimeout(()=>window.print(),400);
  }
<\/script>
</body></html>`;

  const w = window.open("","_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

const PAYMENT_METHODS = ["Zelle","Cash App","PayPal","Venmo","Cash","Other"];
function InvoiceView({inv,clients,projects,onX,onLogPayment}) {
  const cl = clients.find(c=>String(c.id)===String(inv.clientId));
  const pr = projects.find(p=>String(p.id)===String(inv.projectId));
  const cf = INV_STATUS[inv.status]||INV_STATUS.draft;
  const clientName  = cl?.name || inv.clientName || "—";
  const clientEmail = cl?.email || inv.clientEmail || "";
  const clientPhone = cl?.phone || "";

  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmt,  setPayAmt]  = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0,10));
  const [payMeth, setPayMeth] = useState("Zelle");
  const [payNote, setPayNote] = useState("");
  const [payErr,  setPayErr]  = useState("");

  const payments   = Array.isArray(inv.payments) ? inv.payments : [];
  const amountPaid = inv.amountPaid != null ? Number(inv.amountPaid) : Number(inv.depositPaid || 0);
  const balanceDue = Number(inv.balanceDue || 0);
  const isPaid     = inv.status === "paid";

  const submitPayment = () => {
    const amt = Number(payAmt);
    if (!amt || amt <= 0) { setPayErr("Enter a valid amount."); return; }
    if (amt > balanceDue + 0.01) { setPayErr(`Amount exceeds balance due (${$(balanceDue)}).`); return; }
    onLogPayment(inv.id, { amount: amt, date: payDate, method: payMeth, note: payNote });
    setPayAmt(""); setPayNote(""); setPayErr(""); setShowPayForm(false);
  };

  return (
    <div style={C.overlay} onClick={onX}>
      <div style={{...C.mbox,maxWidth:660,width:"95%"}} onClick={e=>e.stopPropagation()}>
        <div style={{...C.mhead,background:T.dark}}>
          <span style={{fontWeight:700,fontSize:14,color:"#f8f4ee"}}>Invoice Preview · Vista Previa</span>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {!isPaid && (
              <button
                onClick={()=>setShowPayForm(v=>!v)}
                style={{background:"#fb923c",color:"#fff",border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                💵 Log Payment
              </button>
            )}
            <button
              onClick={()=>printInvoice(inv,clientName,clientEmail,clientPhone,pr?.title,pr?.date)}
              style={{background:"#2A9D8F",color:"#fff",border:"none",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              📄 PDF / Share
            </button>
            <button style={{...C.xBtn,color:T.light}} onClick={onX}><IconX/></button>
          </div>
        </div>
        <div style={{padding:"22px 24px",maxHeight:"80vh",overflowY:"auto",fontSize:13}}>

          {/* Bazani header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18,paddingBottom:16,borderBottom:`2px solid #eef7f6`}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <img src={APP_LOGO} alt="Bazani Creations" style={{height:36,width:"auto",objectFit:"contain"}}/>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:"#2A9D8F",letterSpacing:.5}}>Bazani Creations</div>
                  <div style={{fontSize:10,color:T.mid}}>Custom Event Decor · Columbus, Ohio</div>
                </div>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:18,fontWeight:800,color:T.dark}}>{inv.invoiceNumber}</div>
              <div style={{marginTop:4}}><Bdg cfg={cf}>{cf.label}</Bdg></div>
              <div style={{fontSize:10,color:T.mid,marginTop:4}}>
                Issued: {inv.issueDate||"—"}
                {inv.dueDate && <span> · Due: {inv.dueDate}</span>}
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div style={{background:T.cream,borderRadius:8,padding:"10px 14px",marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:700,color:"#2A9D8F",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Bill To / Facturar a</div>
            <div style={{fontWeight:700,fontSize:15,color:T.dark}}>{clientName}</div>
            {clientEmail && <div style={{color:T.mid,fontSize:12,marginTop:1}}>{clientEmail}{clientPhone?` · ${clientPhone}`:""}</div>}
            {pr && <div style={{color:T.mid,fontSize:12,marginTop:3}}>📅 Event: {pr.title} · {pr.date}</div>}
          </div>

          {/* Line Items */}
          {(inv.lineItems||[]).length===0
            ? <div style={{textAlign:"center",color:T.light,padding:"20px 0",fontSize:12}}>No line items on this invoice</div>
            : <table style={{width:"100%",borderCollapse:"collapse",marginBottom:4}}>
                <thead>
                  <tr style={{background:"#eef7f6"}}>
                    {["Service / Package","Qty","Unit Price","Amount"].map((h,i)=>(
                      <th key={h} style={{padding:"7px 10px",fontSize:10,fontWeight:700,color:"#2A9D8F",textTransform:"uppercase",textAlign:i===0?"left":"right"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(inv.lineItems||[]).map(li=>(
                    <tr key={li.id} style={{borderBottom:`1px solid #f0ebe2`}}>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          {li.isPackage&&<span style={{width:7,height:7,borderRadius:"50%",background:li.color||"#2A9D8F",display:"inline-block",flexShrink:0}}/>}
                          <span style={{fontWeight:700,color:li.isPackage?"#2A9D8F":T.dark}}>{li.name}</span>
                          {li.isPackage&&<span style={{fontSize:9,background:"#eef7f6",color:"#2A9D8F",padding:"1px 5px",borderRadius:8,fontWeight:700}}>PKG</span>}
                        </div>
                        {li.isPackage&&li.includes&&li.includes.map((item,i)=>(
                          <div key={i} style={{fontSize:10,color:T.mid,paddingLeft:12,lineHeight:1.5}}>✓ {item}</div>
                        ))}
                        {!li.isPackage&&li.es&&li.es!==li.name&&<div style={{fontSize:10,color:"#a07090",paddingLeft:0}}>{li.es}</div>}
                      </td>
                      <td style={{padding:"9px 10px",textAlign:"right",color:"#666"}}>{li.qty}</td>
                      <td style={{padding:"9px 10px",textAlign:"right",color:"#666"}}>{$(li.price)}</td>
                      <td style={{padding:"9px 10px",textAlign:"right",fontWeight:700,color:T.dark}}>{$(Number(li.price||0)*Number(li.qty||1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }

          {/* Totals */}
          <div style={{background:"#eef7f6",borderRadius:8,padding:"12px 14px",margin:"10px 0"}}>
            <TR l="Subtotal" v={$(inv.subtotal||inv.total||0)}/>
            <TR l="TOTAL" v={$(inv.total||0)} bold big/>
            <TR l="Amount Paid" v={`– ${$(amountPaid)}`} dim/>
            <TR l="Balance Due" v={$(balanceDue)} bold clr={balanceDue>0?"#E76F51":T.green}/>
          </div>

          {/* Payment history */}
          {payments.length > 0 && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Payment History</div>
              {payments.map((p,i)=>(
                <div key={p.id||i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",background:"#f0fdf4",borderRadius:7,marginBottom:4,fontSize:12}}>
                  <div>
                    <span style={{fontWeight:700,color:T.dark}}>{$(p.amount)}</span>
                    <span style={{color:T.mid,marginLeft:6}}>{p.method}</span>
                    {p.note && <span style={{color:T.mid,marginLeft:6,fontStyle:"italic"}}>· {p.note}</span>}
                  </div>
                  <div style={{color:T.mid,fontSize:11}}>{p.date}</div>
                </div>
              ))}
            </div>
          )}

          {/* Log Payment form */}
          {showPayForm && (
            <div style={{background:"#fff7ed",border:"1px solid #fb923c40",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:"#c2410c",marginBottom:10}}>Log a Payment</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.mid,marginBottom:3}}>AMOUNT ($)</div>
                  <input type="number" min="0" step="0.01" value={payAmt} onChange={e=>setPayAmt(e.target.value)}
                    placeholder={$(balanceDue)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:"1px solid #fed7aa",background:"#fff",fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.mid,marginBottom:3}}>DATE</div>
                  <input type="date" value={payDate} onChange={e=>setPayDate(e.target.value)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:"1px solid #fed7aa",background:"#fff",fontSize:13,boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.mid,marginBottom:3}}>METHOD</div>
                  <select value={payMeth} onChange={e=>setPayMeth(e.target.value)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:"1px solid #fed7aa",background:"#fff",fontSize:13,boxSizing:"border-box"}}>
                    {PAYMENT_METHODS.map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:T.mid,marginBottom:3}}>NOTE (optional)</div>
                  <input value={payNote} onChange={e=>setPayNote(e.target.value)}
                    placeholder="e.g. deposit, final payment…"
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:"1px solid #fed7aa",background:"#fff",fontSize:13,boxSizing:"border-box"}}/>
                </div>
              </div>
              {payErr && <div style={{color:"#dc2626",fontSize:11,marginBottom:6}}>{payErr}</div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={submitPayment}
                  style={{flex:1,background:"#fb923c",color:"#fff",border:"none",borderRadius:7,padding:"9px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  Save Payment
                </button>
                <button onClick={()=>{setShowPayForm(false);setPayErr("");}}
                  style={{flex:1,background:"none",border:"1px solid #fb923c",color:"#c2410c",borderRadius:7,padding:"9px",fontWeight:600,fontSize:13,cursor:"pointer"}}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {inv.notes&&<div style={{fontStyle:"italic",color:"#555",fontSize:12,marginBottom:10,padding:"8px 12px",background:T.cream,borderRadius:7}}><b>Note:</b> {inv.notes}</div>}

          <div style={{background:"#f0fdf4",borderRadius:7,padding:"8px 12px",fontSize:11,color:"#1a4a2a",marginBottom:10}}>
            <b>Payment accepted / Aceptamos:</b> Zelle · Cash App · PayPal · Venmo
          </div>

          {inv.includeContract&&(
            <pre style={{fontSize:10,color:"#999",borderTop:`1px solid ${T.creamD}`,paddingTop:12,whiteSpace:"pre-wrap",fontFamily:T.fontDisplay,lineHeight:1.7,margin:0}}>
              {CONTRACT_TERMS}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CLIENTS ─────────────────────────────────────────────────────────────────
function Clis({clients,projects,bookings,onEdit,onNew}) {
  const [q,setQ] = useState("");
  const shown    = clients.filter(c=>c.name.toLowerCase().includes(q.toLowerCase())||c.email.toLowerCase().includes(q.toLowerCase()));

  const exportClients = () => {
    const rows = shown.map(c=>({
      Name: c.name, Phone: c.phone, Email: c.email,
      Language: c.lang==="es"?"Spanish":"English",
      Bookings: bookings.filter(b=>b.clientId===c.id).length,
      Notes: c.notes||"",
    }));
    downloadCSV("bazani-clients.csv", toCSV(rows, ["Name","Phone","Email","Language","Events","Notes"]));
  };

  const exportMailingList = () => {
    const rows = clients.filter(c=>c.email).map(c=>({
      Name: c.name, Email: c.email, Phone: c.phone,
      Language: c.lang==="es"?"Spanish":"English",
    }));
    downloadCSV("bazani-mailing-list.csv", toCSV(rows, ["Name","Email","Phone","Language"]));
  };

  return (
    <div>
      <PH title="Clients · Clientes" sub={`${clients.length} clients`} action={{lbl:"+ New Client",fn:onNew}}/>
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <input style={{...C.search,marginBottom:0,flex:1,minWidth:200}} placeholder="Search · Buscar…" value={q} onChange={e=>setQ(e.target.value)}/>
        <button style={C.tealBtn} onClick={exportClients}>⬇ Export Clients CSV</button>
        <button style={{...C.tealBtn,background:"#f5a623"}} onClick={exportMailingList}>✉ Export Mailing List CSV</button>
      </div>
      <div style={C.tblWrap}>
        <table style={C.tbl}>
          <thead><tr>{["Name","Phone","Email","Lang","Bookings",""].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
          <tbody>
            {shown.map(c=>{
              const bkCount=(bookings||[]).filter(b=>b.clientId===c.id).length;
              return <tr key={c.id} style={C.tr}>
                <td style={C.td}><b>{c.name}</b></td>
                <td style={C.td}>{c.phone}</td>
                <td style={C.td}>{c.email}</td>
                <td style={C.td}><span style={C.langBdg}>{c.lang==="es"?"🇲🇽 ES":"🇺🇸 EN"}</span></td>
                <td style={C.td}>{bkCount}</td>
                <td style={C.td}><button style={C.editBtn} onClick={()=>onEdit(c)}>Edit</button></td>
              </tr>;
            })}
          </tbody>
        </table>
        {!shown.length&&<ES emoji="👥" title="No clients found" sub="Add your first client"/>}
      </div>
    </div>
  );
}

// ─── PROJECTS (3 views: Cards · Calendar · List) ─────────────────────────────
function Projs({projects,clients,inventory,onEdit,onNew}) {
  const [view, setView] = useState("cards"); // cards | calendar | list
  const [statusF, setStatusF] = useState("all");

  const filtered = statusF==="all" ? projects : projects.filter(p=>p.status===statusF);

  const viewBtns = [
    {k:"cards",    icon:"⊞", lbl:"Cards"},
    {k:"calendar", icon:"📅", lbl:"Calendar"},
    {k:"list",     icon:"☰",  lbl:"Manage"},
  ];

  return (
    <div>
      <PH title="Events · Eventos" sub={`${projects.length} events`} action={{lbl:"+ New Event",fn:onNew}}/>

      {/* View switcher + status filter */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        {/* Status filter */}
        <div style={C.filters}>
          {["all",...Object.keys(PROJ_STATUS)].map(s=>(
            <button key={s} style={{...C.fBtn,...(statusF===s?C.fOn:{})}} onClick={()=>setStatusF(s)}>
              {s==="all"?"All":PROJ_STATUS[s].label}
            </button>
          ))}
        </div>
        {/* View toggle */}
        <div style={{display:"flex",gap:4,background:"#f5eaf0",borderRadius:8,padding:3}}>
          {viewBtns.map(v=>(
            <button key={v.k}
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
                background:view===v.k?"#fff":"transparent",
                color:view===v.k?T.coral:"#9060a0",
                boxShadow:view===v.k?"0 1px 4px rgba(0,0,0,.10)":"none",
                transition:"all .15s"}}
              onClick={()=>setView(v.k)}>
              <span>{v.icon}</span><span>{v.lbl}</span>
            </button>
          ))}
        </div>
      </div>

      {view==="cards"    && <CardsView    projects={filtered} clients={clients} onEdit={onEdit}/>}
      {view==="calendar" && <CalendarView projects={filtered} clients={clients} onEdit={onEdit}/>}
      {view==="list"     && <ListView     projects={filtered} clients={clients} onEdit={onEdit}/>}
    </div>
  );
}

// ── Cards view (original) ────────────────────────────────────────────────────
function CardsView({projects,clients,onEdit}) {
  return (
    <div style={C.grid}>
      {projects.map(p=><PC key={p.id} p={p} clients={clients} onClick={()=>onEdit(p)}/>)}
      {!projects.length&&<ES emoji="🎉" title="No events yet" sub="Sin eventos aún"/>}
    </div>
  );
}

// ── Calendar view ────────────────────────────────────────────────────────────
function CalendarView({projects,clients,onEdit}) {
  const today = new Date();
  const [yr,  setYr]  = useState(today.getFullYear());
  const [mo,  setMo]  = useState(today.getMonth()); // 0-indexed

  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const firstDay  = new Date(yr, mo, 1).getDay();       // 0=Sun
  const daysInMo  = new Date(yr, mo+1, 0).getDate();
  const prevDays  = new Date(yr, mo, 0).getDate();       // days in prev month

  // map date string → events
  const evtMap = {};
  projects.forEach(p => {
    if (!p.date) return;
    evtMap[p.date] = evtMap[p.date] ? [...evtMap[p.date], p] : [p];
  });

  const pad = n => String(n).padStart(2,"0");
  const dateKey = (y,m,d) => `${y}-${pad(m+1)}-${pad(d)}`;
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());

  const prevMonth = () => { if(mo===0){setMo(11);setYr(y=>y-1);}else setMo(m=>m-1); };
  const nextMonth = () => { if(mo===11){setMo(0);setYr(y=>y+1);}else setMo(m=>m+1); };

  // build cell array: prefix with trailing prev-month days, then current, then fill to 6 rows
  const cells = [];
  for (let i=0;i<firstDay;i++) cells.push({day:prevDays-firstDay+1+i, cur:false, key:null});
  for (let d=1;d<=daysInMo;d++) cells.push({day:d, cur:true, key:dateKey(yr,mo,d)});
  while (cells.length % 7 !== 0) cells.push({day:cells.length-firstDay-daysInMo+1, cur:false, key:null});

  // legend
  const legend = Object.entries(PROJ_STATUS);

  return (
    <div>
      {/* Nav bar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <button style={C.calNavBtn} onClick={prevMonth}>‹</button>
        <span style={{fontWeight:700,fontSize:16,color:"#2d1f2e"}}>{MONTHS[mo]} {yr}</span>
        <button style={C.calNavBtn} onClick={nextMonth}>›</button>
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:10}}>
        {legend.map(([k,v])=>(
          <span key={k} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#666"}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:v.color,display:"inline-block"}}></span>
            {v.label}
          </span>
        ))}
        <span style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#666"}}>
          <span style={{width:10,height:10,borderRadius:"50%",background:"#f0e8f5",border:`1px solid #d8c0e0`,display:"inline-block"}}></span>
          Available
        </span>
      </div>

      {/* Day headers */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
        {DAYS.map(d=>(
          <div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:"#a07090",padding:"4px 0",textTransform:"uppercase",letterSpacing:.5}}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
        {cells.map((cell,idx)=>{
          const evts    = cell.key ? (evtMap[cell.key]||[]) : [];
          const isToday = cell.key===todayKey;
          const booked  = evts.length>0;
          return (
            <div key={idx} style={{
              minHeight:72,
              borderRadius:7,
              border: isToday ? `2px solid ${T.coral}` : "1px solid #f0e0ea",
              background: !cell.cur ? "#faf5fb" : booked ? "#fff5fb" : "#fff",
              padding:"4px 5px",
              opacity: cell.cur ? 1 : 0.4,
            }}>
              <div style={{
                fontSize:12, fontWeight: isToday?800:400,
                color: isToday?T.coral: cell.cur?"#2d1f2e":"#bbb",
                marginBottom:3,
                display:"flex", justifyContent:"space-between", alignItems:"center",
              }}>
                <span>{cell.day}</span>
                {isToday&&<span style={{fontSize:8,fontWeight:700,color:T.coral,letterSpacing:.5}}>TODAY</span>}
              </div>
              {evts.map(p=>{
                const cl  = clients.find(c=>c.id===p.clientId);
                const cfg = PROJ_STATUS[p.status]||PROJ_STATUS.inquiry;
                return (
                  <div key={p.id}
                    onClick={()=>cell.cur&&onEdit(p)}
                    style={{
                      background:cfg.bg,
                      borderLeft:`3px solid ${cfg.color}`,
                      borderRadius:"0 4px 4px 0",
                      padding:"2px 5px",
                      marginBottom:2,
                      cursor:"pointer",
                      fontSize:10,
                      lineHeight:1.3,
                    }}>
                    <div style={{fontWeight:700,color:cfg.color,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cl?.name||"—"}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── List / Manage view ───────────────────────────────────────────────────────
function ListView({projects,clients,onEdit}) {
  const grouped = Object.entries(PROJ_STATUS).map(([key,cfg])=>({
    key, cfg, items: projects.filter(p=>p.status===key)
  }));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {grouped.map(({key,cfg,items})=>(
        <div key={key}>
          {/* Section header */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{width:10,height:10,borderRadius:"50%",background:cfg.color,flexShrink:0,display:"inline-block"}}></span>
            <span style={{fontWeight:700,fontSize:13,color:cfg.color}}>{cfg.label}</span>
            <span style={{fontSize:11,color:"#bbb",marginLeft:2}}>({items.length})</span>
            <div style={{flex:1,height:1,background:"#f0e0ea",marginLeft:4}}></div>
          </div>
          {!items.length
            ? <div style={{fontSize:11,color:"#d0b0c0",paddingLeft:18,paddingBottom:4}}>None</div>
            : <div style={C.tblWrap}>
                <table style={C.tbl}>
                  <thead>
                    <tr>{["Event","Client","Date","Venue","Type","Total",""].map(h=><TH key={h}>{h}</TH>)}</tr>
                  </thead>
                  <tbody>
                    {items
                      .sort((a,b)=>new Date(a.date)-new Date(b.date))
                      .map(p=>{
                        const cl=clients.find(c=>c.id===p.clientId);
                        return (
                          <tr key={p.id} style={C.tr}>
                            <td style={{...C.td,fontWeight:700}}>{p.title}</td>
                            <td style={C.td}>{cl?.name||"—"}</td>
                            <td style={{...C.td,fontSize:12}}>{p.date||"—"}</td>
                            <td style={{...C.td,fontSize:12,color:T.mid}}>{p.venue||"—"}</td>
                            <td style={C.td}><span style={{...C.catBdg,color:"#a060c0",background:"#f5eef9"}}>{p.type}</span></td>
                            <td style={{...C.td,fontWeight:700,color:T.green}}>${Number(p.total||0).toFixed(2)}</td>
                            <td style={C.td}><button style={C.editBtn} onClick={()=>onEdit(p)}>Edit</button></td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </div>
      ))}
    </div>
  );
}

// ─── INVENTORY ───────────────────────────────────────────────────────────────
function Stock({inventory,onEdit,onNew}) {
  const [f,setF]=useState("all");
  const shown=f==="all"?inventory:inventory.filter(i=>i.category===f);
  return (
    <div>
      <PH title="Inventory · Inventario" sub={`${inventory.length} items`} action={{lbl:"+ Add Item",fn:onNew}}/>
      <div style={C.filters}>
        {["all",...INV_CATS].map(c=>(
          <button key={c} style={{...C.fBtn,...(f===c?C.fOn:{})}} onClick={()=>setF(c)}>{c==="all"?"All":c}</button>
        ))}
      </div>
      <div style={C.tblWrap}>
        <table style={C.tbl}>
          <thead><tr>{["Item","Category","Total","Available","Condition","Notes",""].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
          <tbody>
            {shown.map(item=>(
              <tr key={item.id} style={C.tr}>
                <td style={C.td}><b>{item.name}</b></td>
                <td style={C.td}><span style={C.catBdg}>{item.category}</span></td>
                <td style={C.td}>{item.qty}</td>
                <td style={C.td}><span style={{color:item.available<2?"#fb923c":T.green,fontWeight:700}}>{item.available}</span></td>
                <td style={C.td}>{item.condition}</td>
                <td style={{...C.td,fontSize:12,color:"#999"}}>{item.notes}</td>
                <td style={C.td}><button style={C.editBtn} onClick={()=>onEdit(item)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length&&<ES emoji="📦" title="No items" sub="Add inventory items"/>}
      </div>
    </div>
  );
}

// ─── MODALS ──────────────────────────────────────────────────────────────────
function MW({title,onX,children}) {
  const isMobile = window.innerWidth < 640;
  const boxStyle = isMobile
    ? {background:"#fff",width:"100%",height:"100%",maxHeight:"100vh",overflowY:"auto",position:"fixed",inset:0,zIndex:101}
    : {background:"#fff",borderRadius:13,width:"100%",maxWidth:520,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 10px 50px rgba(0,0,0,.22)"};
  return (
    <div style={{...C.overlay,alignItems:isMobile?"flex-end":"center"}} onClick={onX}>
      <div style={boxStyle} onClick={e=>e.stopPropagation()}>
        <div style={C.mhead}><span style={{fontWeight:700,fontSize:14}}>{title}</span><button style={C.xBtn} onClick={onX}><IconX/></button></div>
        <div style={{padding:"16px 20px"}}>{children}</div>
      </div>
    </div>
  );
}
function ClientMod({data,onSave,onDel,onX}) {
  const [f,sf]=useState(data||{name:"",phone:"",email:"",lang:"en",notes:""});
  const s=(k,v)=>sf(x=>({...x,[k]:v}));
  return <MW title={data?"Edit Client":"New Client"} onX={onX}>
    <FL lbl="Full Name"><input style={C.inp} value={f.name} onChange={e=>s("name",e.target.value)}/></FL>
    <FL lbl="Phone"><input style={C.inp} value={f.phone} onChange={e=>s("phone",e.target.value)}/></FL>
    <FL lbl="Email"><input style={C.inp} value={f.email} onChange={e=>s("email",e.target.value)}/></FL>
    <FL lbl="Language"><select style={C.inp} value={f.lang} onChange={e=>s("lang",e.target.value)}><option value="en">🇺🇸 English</option><option value="es">🇲🇽 Español</option></select></FL>
    <FL lbl="Notes"><textarea style={{...C.inp,height:56}} value={f.notes} onChange={e=>s("notes",e.target.value)}/></FL>
    <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
  </MW>;
}
function ProjMod({data,onSave,onDel,onX,clients,inventory}) {
  const [f,sf]=useState(data||{clientId:"",title:"",type:"Quinceañera",date:"",venue:"",theme:"",status:"inquiry",deposit:0,total:0,items:[]});
  const s=(k,v)=>sf(x=>({...x,[k]:v}));
  const tog=id=>sf(x=>({...x,items:x.items.includes(id)?x.items.filter(y=>y!==id):[...x.items,id]}));
  return <MW title={data?"Edit Event":"New Event"} onX={onX}>
    <FL lbl="Client"><select style={C.inp} value={f.clientId} onChange={e=>s("clientId",e.target.value)}><option value="">— Select —</option>{clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></FL>
    <FL lbl="Title"><input style={C.inp} value={f.title} onChange={e=>s("title",e.target.value)}/></FL>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
      <FL lbl="Type"><select style={C.inp} value={f.type} onChange={e=>s("type",e.target.value)}>{EVENT_TYPES.map(t=><option key={t}>{t}</option>)}</select></FL>
      <FL lbl="Status"><select style={C.inp} value={f.status} onChange={e=>s("status",e.target.value)}>{Object.entries(PROJ_STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}</select></FL>
      <FL lbl="Date"><input type="date" style={C.inp} value={f.date} onChange={e=>s("date",e.target.value)}/></FL>
      <FL lbl="Venue"><input style={C.inp} value={f.venue} onChange={e=>s("venue",e.target.value)}/></FL>
      <FL lbl="Deposit ($)"><input type="number" style={C.inp} value={f.deposit} onChange={e=>s("deposit",e.target.value)}/></FL>
      <FL lbl="Total ($)"><input type="number" style={C.inp} value={f.total} onChange={e=>s("total",e.target.value)}/></FL>
    </div>
    <FL lbl="Theme"><input style={C.inp} value={f.theme} onChange={e=>s("theme",e.target.value)}/></FL>
    <FL lbl="Inventory">
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
        {inventory.map(i=><label key={i.id} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:7,fontSize:12,background:f.items.includes(i.id)?"#f9a8d4":"#f5f5f5",cursor:"pointer"}}>
          <input type="checkbox" checked={f.items.includes(i.id)} onChange={()=>tog(i.id)}/>{i.name.split(" / ")[0]}
        </label>)}
      </div>
    </FL>
    <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
  </MW>;
}
function InvMod({data,onSave,onDel,onX}) {
  const [f,sf]=useState(data||{name:"",category:"Globos",qty:1,available:1,condition:"good",notes:""});
  const s=(k,v)=>sf(x=>({...x,[k]:v}));
  return <MW title={data?"Edit Item":"New Item"} onX={onX}>
    <FL lbl="Name (EN / ES)"><input style={C.inp} value={f.name} onChange={e=>s("name",e.target.value)} placeholder="Balloon Arch / Arco de Globos"/></FL>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
      <FL lbl="Category"><select style={C.inp} value={f.category} onChange={e=>s("category",e.target.value)}>{INV_CATS.map(c=><option key={c}>{c}</option>)}</select></FL>
      <FL lbl="Condition"><select style={C.inp} value={f.condition} onChange={e=>s("condition",e.target.value)}>{["good","fair","needs-repair","retired"].map(c=><option key={c}>{c}</option>)}</select></FL>
      <FL lbl="Total Qty"><input type="number" style={C.inp} value={f.qty} onChange={e=>s("qty",e.target.value)}/></FL>
      <FL lbl="Available"><input type="number" style={C.inp} value={f.available} onChange={e=>s("available",e.target.value)}/></FL>
    </div>
    <FL lbl="Notes"><input style={C.inp} value={f.notes} onChange={e=>s("notes",e.target.value)}/></FL>
    <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
  </MW>;
}

// ─── EXPENSES PAGE ────────────────────────────────────────────────────────────
function Exps({expenses,onEdit,onNew,onViewRcpt}) {
  const [catF,  setCatF]  = useState("all");
  const [yearF, setYearF] = useState("all");
  const [typeF, setTypeF] = useState("all"); // all | opex | capex

  const years = Array.from(new Set(expenses.map(e=>(e.date||"").slice(0,4)).filter(Boolean))).sort().reverse();

  const shown = expenses.filter(e => {
    const cat   = EXP_CATS.find(c=>c.id===e.category);
    if (catF!=="all" && e.category!==catF) return false;
    if (yearF!=="all" && (e.date||"").slice(0,4)!==yearF) return false;
    if (typeF==="capex" && !cat?.capex) return false;
    if (typeF==="opex"  &&  cat?.capex) return false;
    return true;
  }).sort((a,b)=>new Date(b.date||0)-new Date(a.date||0));

  const totalShown  = shown.reduce((s,e)=>s+Number(e.amount||0),0);
  const totalCapex  = shown.filter(e=>EXP_CATS.find(c=>c.id===e.category)?.capex).reduce((s,e)=>s+Number(e.amount||0),0);
  const totalOpex   = totalShown - totalCapex;

  // breakdown by category for shown
  const byCategory = EXP_CATS.map(cat=>({
    ...cat,
    total: shown.filter(e=>e.category===cat.id).reduce((s,e)=>s+Number(e.amount||0),0),
    count: shown.filter(e=>e.category===cat.id).length,
  })).filter(c=>c.count>0);

  return (
    <div>
      <PH title="Expenses · Gastos" sub={`${expenses.length} total entries`} action={{lbl:"+ Add Expense",fn:onNew}}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12,marginTop:-16}}>
        <button style={C.tealBtn} onClick={()=>{
          const rows = expenses.map(e=>{
            const cat=EXP_CATS.find(c=>c.id===e.category)||EXP_CATS[5];
            return { Date:e.date||"", Category:cat.label, Type:cat.capex?"CapEx":"OpEx", Item:e.item||"", Description:e.description||"", Amount:Number(e.amount||0).toFixed(2), Receipt:e.receipt?"Yes":"No" };
          });
          downloadCSV("bazani-expenses.csv", toCSV(rows,["Date","Category","Type","Item","Description","Amount","Receipt"]));
        }}>⬇ Export Expenses CSV</button>
      </div>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:22}}>
        {[
          {lbl:"Total Expenses",   sub:"All filtered",         val:`$${totalShown.toFixed(2)}`,  clr:"#f472b6"},
          {lbl:"Operating (OpEx)", sub:"Regular expenses",     val:`$${totalOpex.toFixed(2)}`,   clr:T.blue},
          {lbl:"Capital (CapEx)",  sub:"Equipment & Assets",   val:`$${totalCapex.toFixed(2)}`,  clr:"#a78bfa"},
        ].map(s=>(
          <div key={s.lbl} style={{...C.card,borderTop:`3px solid ${s.clr}`,padding:"14px 16px"}}>
            <div style={{fontSize:22,fontWeight:800,color:s.clr,marginBottom:2}}>{s.val}</div>
            <div style={{fontSize:12,fontWeight:600}}>{s.lbl}</div>
            <div style={{fontSize:11,color:"#a07090"}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Category breakdown bar */}
      {byCategory.length>0 && (
        <div style={{background:"#fff",border:`1px solid #f0e0ea`,borderRadius:10,padding:"14px 16px",marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"#a07090",textTransform:"uppercase",letterSpacing:.6,marginBottom:10}}>Breakdown by Category</div>
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {byCategory.map(cat=>{
              const pct = totalShown>0 ? (cat.total/totalShown)*100 : 0;
              return (
                <div key={cat.id}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                    <span>{cat.icon} {cat.label}{cat.capex&&<span style={{fontSize:10,color:"#a78bfa",marginLeft:5,fontWeight:700}}>CapEx</span>}</span>
                    <span style={{fontWeight:700,color:cat.color}}>${cat.total.toFixed(2)} <span style={{color:"#bbb",fontWeight:400}}>({cat.count})</span></span>
                  </div>
                  <div style={{height:6,borderRadius:3,background:"#f5eaf0",overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:cat.color,borderRadius:3,transition:"width .4s"}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14,alignItems:"center"}}>
        <div style={C.filters}>
          <button style={{...C.fBtn,...(catF==="all"?C.fOn:{})}} onClick={()=>setCatF("all")}>All</button>
          {EXP_CATS.map(c=>(
            <button key={c.id} style={{...C.fBtn,...(catF===c.id?C.fOn:{})}} onClick={()=>setCatF(c.id)}>{c.icon} {c.label}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:5,marginLeft:"auto"}}>
          <select style={{...C.inp,width:"auto",padding:"4px 10px",fontSize:11}} value={typeF} onChange={e=>setTypeF(e.target.value)}>
            <option value="all">All Types</option>
            <option value="opex">OpEx Only</option>
            <option value="capex">CapEx Only</option>
          </select>
          <select style={{...C.inp,width:"auto",padding:"4px 10px",fontSize:11}} value={yearF} onChange={e=>setYearF(e.target.value)}>
            <option value="all">All Years</option>
            {years.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={C.tblWrap}>
        <table style={C.tbl}>
          <thead>
            <tr>{["Date","Category","Item / Description","Amount","Type","Receipt",""].map(h=><TH key={h}>{h}</TH>)}</tr>
          </thead>
          <tbody>
            {shown.map(e=>{
              const cat = EXP_CATS.find(c=>c.id===e.category)||EXP_CATS[5];
              return (
                <tr key={e.id} style={C.tr}>
                  <td style={{...C.td,fontSize:12,color:"#666"}}>{e.date||"—"}</td>
                  <td style={C.td}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:5,background:cat.bg,color:cat.color,padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:600}}>
                      {cat.icon} {cat.label}
                    </span>
                  </td>
                  <td style={C.td}>
                    <div style={{fontWeight:600,fontSize:13}}>{e.item||"—"}</div>
                    {e.description&&<div style={{fontSize:11,color:T.mid,marginTop:1}}>{e.description}</div>}
                  </td>
                  <td style={{...C.td,fontWeight:700,color:T.coral,fontSize:14}}>${Number(e.amount||0).toFixed(2)}</td>
                  <td style={C.td}>
                    <span style={{fontSize:10,fontWeight:700,color:cat.capex?"#a78bfa":T.blue,background:cat.capex?"#f5f3ff":"#eff6ff",padding:"2px 7px",borderRadius:12}}>
                      {cat.capex?"CapEx":"OpEx"}
                    </span>
                  </td>
                  <td style={C.td}>
                    {e.receipt
                      ? <button style={{...C.editBtn,display:"flex",alignItems:"center",gap:4}} onClick={()=>onViewRcpt(e.receipt)}>
                          <IconClip/> View
                        </button>
                      : <span style={{fontSize:11,color:"#ccc"}}>None</span>
                    }
                  </td>
                  <td style={C.td}><button style={C.editBtn} onClick={()=>onEdit(e)}>Edit</button></td>
                </tr>
              );
            })}
          </tbody>
          {shown.length>0&&(
            <tfoot><tr style={{background:"#fdf0f7"}}>
              <td colSpan={3} style={{...C.td,fontWeight:700}}>Total ({shown.length} items)</td>
              <td style={{...C.td,fontWeight:800,color:T.coral}}>${totalShown.toFixed(2)}</td>
              <td colSpan={3}></td>
            </tr></tfoot>
          )}
        </table>
        {!shown.length&&<ES emoji="🌸" title="No expenses yet" sub="Sin gastos aún · Start tracking your spending"/>}
      </div>
    </div>
  );
}

// ─── EXPENSE MODAL ────────────────────────────────────────────────────────────
function ExpMod({data,onSave,onDel,onX}) {
  const blank = { category:"supplies", item:"", description:"", amount:"", date:new Date().toISOString().slice(0,10), receipt:null, receiptName:"" };
  const [f, sf] = useState(data ? {...data} : blank);
  const s = (k,v) => sf(x=>({...x,[k]:v}));

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { s("receipt", ev.target.result); s("receiptName", file.name); };
    reader.readAsDataURL(file);
  };

  const selCat = EXP_CATS.find(c=>c.id===f.category)||EXP_CATS[0];

  return (
    <MW title={data?"Edit Expense · Editar Gasto":"New Expense · Nuevo Gasto"} onX={onX}>
      {/* Category picker */}
      <FL lbl="Category / Categoría">
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:2}}>
          {EXP_CATS.map(cat=>(
            <button key={cat.id}
              onClick={()=>s("category",cat.id)}
              style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,border:`2px solid ${f.category===cat.id?cat.color:"#f0e0ea"}`,background:f.category===cat.id?cat.bg:"#fff",cursor:"pointer",fontSize:11,fontWeight:f.category===cat.id?700:400,color:f.category===cat.id?cat.color:T.mid,transition:"all .12s"}}>
              {cat.icon} {cat.label}
              {cat.capex&&<span style={{fontSize:9,color:"#a78bfa",fontWeight:700}}>CapEx</span>}
            </button>
          ))}
        </div>
      </FL>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <FL lbl="Date / Fecha">
          <input type="date" style={C.inp} value={f.date} onChange={e=>s("date",e.target.value)}/>
        </FL>
        <FL lbl="Amount / Monto ($)">
          <input type="number" style={C.inp} value={f.amount} onChange={e=>s("amount",e.target.value)} placeholder="0.00" step="0.01"/>
        </FL>
      </div>

      <FL lbl="Item Name / Nombre del Artículo">
        <input style={C.inp} value={f.item} onChange={e=>s("item",e.target.value)} placeholder="e.g. Balloon rolls, Printer ink…"/>
      </FL>

      <FL lbl="Description / Descripción">
        <textarea style={{...C.inp,height:60}} value={f.description} onChange={e=>s("description",e.target.value)} placeholder="Where purchased, what it was for…"/>
      </FL>

      <FL lbl="Receipt Photo / Foto del Recibo">
        <div style={{border:"2px dashed #f0c0d8",borderRadius:9,padding:"14px",textAlign:"center",background:"#fdf8fb",cursor:"pointer",position:"relative"}}
          onClick={()=>document.getElementById("rcpt-upload").click()}>
          {f.receipt
            ? <div>
                <img src={f.receipt} alt="receipt" style={{maxHeight:120,maxWidth:"100%",borderRadius:6,objectFit:"contain"}}/>
                <div style={{fontSize:11,color:"#a07090",marginTop:6}}>{f.receiptName}</div>
                <button style={{...C.editBtn,fontSize:10,marginTop:6}} onClick={e=>{e.stopPropagation();s("receipt",null);s("receiptName","");}}>✕ Remove</button>
              </div>
            : <div>
                <div style={{fontSize:22,marginBottom:4}}>📎</div>
                <div style={{fontSize:12,color:"#c890b0",fontWeight:600}}>Tap to upload receipt photo</div>
                <div style={{fontSize:10,color:"#d0b0c0",marginTop:2}}>JPG, PNG, PDF supported</div>
              </div>
          }
          <input id="rcpt-upload" type="file" accept="image/*,application/pdf" style={{display:"none"}} onChange={handleFile}/>
        </div>
      </FL>

      <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
    </MW>
  );
}

// ─── RECEIPT VIEWER ───────────────────────────────────────────────────────────
function RcptViewer({src,onX}) {
  const isImg = src && (src.startsWith("data:image")||src.includes("png")||src.includes("jpg")||src.includes("jpeg")||src.includes("webp"));
  return (
    <div style={C.overlay} onClick={onX}>
      <div style={{...C.mbox,maxWidth:520,width:"92%"}} onClick={e=>e.stopPropagation()}>
        <div style={C.mhead}>
          <span style={{fontWeight:700,fontSize:14}}>Receipt · Recibo</span>
          <button style={C.xBtn} onClick={onX}><IconX/></button>
        </div>
        <div style={{padding:"16px",textAlign:"center"}}>
          {isImg
            ? <img src={src} alt="receipt" style={{maxWidth:"100%",maxHeight:"65vh",borderRadius:8,objectFit:"contain"}}/>
            : <iframe src={src} style={{width:"100%",height:"60vh",border:"none",borderRadius:8}} title="receipt"/>
          }
        </div>
      </div>
    </div>
  );
}

// ─── FINANCES PAGE ────────────────────────────────────────────────────────────
function Finances({invoices, expenses}) {
  const [subTab, setSubTab] = useState("pl");
  const [year,   setYear]   = useState(String(new Date().getFullYear()));

  const currentYearStr = String(new Date().getFullYear());
  const years = Array.from(new Set([
    currentYearStr,
    ...invoices.map(v=>(v.issueDate||"").slice(0,4)),
    ...invoices.map(v=>v.ts ? String(new Date(v.ts).getFullYear()) : ""),
    ...expenses.map(e=>(e.date||"").slice(0,4)),
  ].filter(Boolean))).sort().reverse();
  if (!years.includes(year) && years.length) setYear(years[0]);

  // ── helpers ────────────────────────────────────────────────────────────────
  const invYear  = invoices.filter(v=>(v.issueDate||"").startsWith(year) || (!v.issueDate && String(new Date(v.ts||0).getFullYear())===year));
  const expYear  = expenses.filter(e=>(e.date||"").startsWith(year));

  // Income = fully paid invoices + deposits on partial invoices
  const income   = invYear.filter(v=>v.status==="paid").reduce((s,v)=>s+Number(v.total||0),0)
                 + invYear.filter(v=>v.status==="partial").reduce((s,v)=>s+Number(v.depositPaid||0),0);
  const pending  = invYear.filter(v=>["sent","partial"].includes(v.status)).reduce((s,v)=>s+Number(v.balanceDue||0),0);
  const totalExp = expYear.reduce((s,e)=>s+Number(e.amount||0),0);
  const capex    = expYear.filter(e=>EXP_CATS.find(c=>c.id===e.category)?.capex).reduce((s,e)=>s+Number(e.amount||0),0);
  const opex     = totalExp - capex;
  const netProfit= income - opex;
  const margin   = income>0 ? ((netProfit/income)*100).toFixed(1) : "0.0";

  // Monthly buckets
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const monthly = MONTHS.map((m,idx)=>{
    const mo   = String(idx+1).padStart(2,"0");
    const key  = `${year}-${mo}`;
    const inc  = invYear.filter(v=>v.status==="paid"&&(v.issueDate||"").startsWith(key)).reduce((s,v)=>s+Number(v.total||0),0)
              + invYear.filter(v=>v.status==="partial"&&(v.issueDate||"").startsWith(key)).reduce((s,v)=>s+Number(v.depositPaid||0),0);
    const owed = invYear.filter(v=>["sent","partial"].includes(v.status)&&(v.issueDate||"").startsWith(key)).reduce((s,v)=>s+Number(v.balanceDue||0),0);
    const exp  = expYear.filter(e=>(e.date||"").startsWith(key)).reduce((s,e)=>s+Number(e.amount||0),0);
    return {m, inc, owed, exp, net:inc-exp};
  });

  // Quarterly tax buckets (assume US: Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec)
  const quarters = [
    {q:"Q1",months:[0,1,2]},{q:"Q2",months:[3,4,5]},{q:"Q3",months:[6,7,8]},{q:"Q4",months:[9,10,11]},
  ].map(({q,months})=>{
    const qInc = months.reduce((s,i)=>s+monthly[i].inc,0);
    const qExp = months.reduce((s,i)=>s+monthly[i].exp,0);
    const qNet = qInc - qExp;
    const estTax = Math.max(0, qNet * 0.25); // rough 25% self-employment estimate
    return {q, qInc, qExp, qNet, estTax};
  });

  const maxBar = Math.max(...monthly.map(m=>Math.max(m.inc,m.exp)),1);

  const subBtns = [{k:"pl",lbl:"Overview"},{k:"exp",lbl:"Expenses"},{k:"tax",lbl:"Tax Summary"}];

  return (
    <div>
      <PH title="Finances" sub={`${year} · Collected ${$(income)} · Outstanding ${$(pending)}`}/>

      {/* Year selector + sub-tabs */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:0,background:"#eef7f6",borderRadius:8,padding:3}}>
          {subBtns.map(b=>(
            <button key={b.k} onClick={()=>setSubTab(b.k)} style={{padding:"5px 14px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,fontWeight:subTab===b.k?700:400,background:subTab===b.k?"#2A9D8F":"transparent",color:subTab===b.k?"#fff":"#2A9D8F",transition:"all .15s"}}>
              {b.lbl}
            </button>
          ))}
        </div>
        <select style={{...C.inp,width:"auto",padding:"5px 12px",fontSize:12}} value={year} onChange={e=>setYear(e.target.value)}>
          {years.length ? years.map(y=><option key={y}>{y}</option>) : <option>{year}</option>}
        </select>
      </div>

      {/* ── P&L REPORT ── */}
      {subTab==="pl" && (
        <div>
          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:12}}>
            {[
              {lbl:"Revenue Collected", sub:"Paid + deposits",    val:$(income),    clr:"#2A9D8F"},
              {lbl:"Outstanding",       sub:"Balance still owed", val:$(pending),   clr:"#E76F51"},
            ].map(s=>(
              <div key={s.lbl} style={{...C.card,borderTop:`3px solid ${s.clr}`,padding:"14px 16px"}}>
                <div style={{fontSize:22,fontWeight:800,color:s.clr}}>{s.val}</div>
                <div style={{fontSize:12,fontWeight:600,marginTop:2}}>{s.lbl}</div>
                <div style={{fontSize:11,color:T.mid}}>{s.sub}</div>
              </div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:20}}>
            {[
              {lbl:"Total Expenses",  sub:"OpEx + CapEx",      val:$(totalExp),  clr:"#a78bfa"},
              {lbl:"Net Profit",      sub:`${margin}% margin`, val:$(netProfit), clr:netProfit>=0?T.green:"#f87171"},
            ].map(s=>(
              <div key={s.lbl} style={{...C.card,borderTop:`3px solid ${s.clr}`,padding:"14px 16px"}}>
                <div style={{fontSize:22,fontWeight:800,color:s.clr}}>{s.val}</div>
                <div style={{fontSize:12,fontWeight:600,marginTop:2}}>{s.lbl}</div>
                <div style={{fontSize:11,color:T.mid}}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Monthly bar chart */}
          <div style={{...C.card,padding:"16px",marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:T.dark,marginBottom:12}}>Monthly Overview · {year}</div>
            <div style={{display:"flex",gap:4,alignItems:"flex-end",height:120}}>
              {monthly.map(({m,inc,owed,exp})=>(
                <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <div style={{width:"100%",display:"flex",gap:1,alignItems:"flex-end",height:100}}>
                    <div style={{flex:1,background:"#2A9D8F",borderRadius:"2px 2px 0 0",height:`${(inc/maxBar)*100}%`,minHeight:inc>0?2:0,transition:"height .3s"}}/>
                    <div style={{flex:1,background:"#F4A261",borderRadius:"2px 2px 0 0",height:`${(owed/maxBar)*100}%`,minHeight:owed>0?2:0,transition:"height .3s"}}/>
                    <div style={{flex:1,background:"#E76F51",borderRadius:"2px 2px 0 0",height:`${(exp/maxBar)*100}%`,minHeight:exp>0?2:0,transition:"height .3s"}}/>
                  </div>
                  <div style={{fontSize:9,color:T.mid,textAlign:"center"}}>{m}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:14,marginTop:8}}>
              {[{clr:"#2A9D8F",lbl:"Collected"},{clr:"#F4A261",lbl:"Outstanding"},{clr:"#E76F51",lbl:"Expenses"}].map(l=>(
                <div key={l.lbl} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.mid}}>
                  <span style={{width:10,height:10,borderRadius:2,background:l.clr,display:"inline-block"}}/>
                  {l.lbl}
                </div>
              ))}
            </div>
          </div>

          {/* P&L table */}
          <div style={C.tblWrap}>
            <table style={C.tbl}>
              <thead><tr>{["Month","Collected","Outstanding","Expenses","Net"].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
              <tbody>
                {monthly.map(({m,inc,owed,exp,net})=>(
                  <tr key={m} style={C.tr}>
                    <td style={C.td}><b>{m} {year}</b></td>
                    <td style={{...C.td,color:"#2A9D8F",fontWeight:600}}>{$(inc)}</td>
                    <td style={{...C.td,color:owed>0?"#E76F51":T.mid,fontWeight:owed>0?600:400}}>{owed>0?$(owed):"—"}</td>
                    <td style={{...C.td,color:"#a78bfa",fontWeight:600}}>{$(exp)}</td>
                    <td style={{...C.td,fontWeight:700,color:net>=0?T.green:"#f87171"}}>{$(net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{background:"#eef7f6"}}>
                  <td style={{...C.td,fontWeight:700}}>TOTAL {year}</td>
                  <td style={{...C.td,fontWeight:800,color:"#2A9D8F"}}>{$(income)}</td>
                  <td style={{...C.td,fontWeight:800,color:pending>0?"#E76F51":T.mid}}>{pending>0?$(pending):"—"}</td>
                  <td style={{...C.td,fontWeight:800,color:"#a78bfa"}}>{$(totalExp)}</td>
                  <td style={{...C.td,fontWeight:800,color:netProfit>=0?T.green:"#f87171"}}>{$(netProfit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── EXPENSES ── */}
      {subTab==="exp" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
            {[
              {lbl:"Total Expenses", val:$(totalExp), clr:"#E76F51"},
              {lbl:"Operating (OpEx)",val:$(opex),   clr:T.blue},
              {lbl:"Capital (CapEx)", val:$(capex),  clr:"#a78bfa"},
            ].map(s=>(
              <div key={s.lbl} style={{...C.card,borderTop:`3px solid ${s.clr}`,padding:"14px 16px"}}>
                <div style={{fontSize:20,fontWeight:800,color:s.clr}}>{s.val}</div>
                <div style={{fontSize:12,fontWeight:600,marginTop:2}}>{s.lbl}</div>
              </div>
            ))}
          </div>
          <div style={C.tblWrap}>
            <table style={C.tbl}>
              <thead><tr>{["Date","Category","Item","Amount","Type"].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
              <tbody>
                {expYear.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(e=>{
                  const cat=EXP_CATS.find(c=>c.id===e.category)||EXP_CATS[5];
                  return (
                    <tr key={e.id} style={C.tr}>
                      <td style={{...C.td,fontSize:11,color:T.mid}}>{e.date||"—"}</td>
                      <td style={C.td}><span style={{background:cat.bg,color:cat.color,padding:"1px 7px",borderRadius:12,fontSize:10,fontWeight:600}}>{cat.icon} {cat.label}</span></td>
                      <td style={C.td}>{e.item||"—"}</td>
                      <td style={{...C.td,fontWeight:700,color:"#E76F51"}}>{$(e.amount)}</td>
                      <td style={C.td}><span style={{fontSize:10,fontWeight:700,color:cat.capex?"#a78bfa":T.blue,background:cat.capex?"#f5f3ff":"#eff6ff",padding:"1px 7px",borderRadius:12}}>{cat.capex?"CapEx":"OpEx"}</span></td>
                    </tr>
                  );
                })}
              </tbody>
              {expYear.length>0&&<tfoot><tr style={{background:"#eef7f6"}}>
                <td colSpan={3} style={{...C.td,fontWeight:700}}>Total ({expYear.length})</td>
                <td style={{...C.td,fontWeight:800,color:"#E76F51"}}>{$(totalExp)}</td>
                <td></td>
              </tr></tfoot>}
            </table>
            {!expYear.length&&<Dim>No expenses for {year}</Dim>}
          </div>
        </div>
      )}

      {/* ── TAX SUMMARY ── */}
      {subTab==="tax" && (
        <div>
          <div style={{...C.card,padding:"14px 16px",marginBottom:16,background:"#fefbf0",border:`1px solid ${T.amber50}`}}>
            <div style={{fontSize:12,fontWeight:700,color:"#b8860b",marginBottom:4}}>⚠️ Estimated Tax — Not Tax Advice</div>
            <div style={{fontSize:11,color:T.mid,lineHeight:1.5}}>These figures use a rough 25% self-employment estimate on net profit per quarter. Always consult a tax professional for accurate filings. / Estas cifras son estimaciones. Consulte a un contador para declaraciones oficiales.</div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
            {quarters.map(q=>(
              <div key={q.q} style={{...C.card,padding:"13px 14px",borderTop:`3px solid ${q.qNet>=0?"#2A9D8F":"#E76F51"}`}}>
                <div style={{fontSize:16,fontWeight:800,color:T.dark,marginBottom:4}}>{q.q}</div>
                <div style={{fontSize:11,color:T.mid,marginBottom:6}}>Net: <b style={{color:q.qNet>=0?"#2A9D8F":"#f87171"}}>{$(q.qNet)}</b></div>
                <div style={{fontSize:12,fontWeight:700,color:T.amber}}>Est. Tax</div>
                <div style={{fontSize:18,fontWeight:800,color:"#b8860b"}}>{$(q.estTax)}</div>
              </div>
            ))}
          </div>

          <div style={C.tblWrap}>
            <table style={C.tbl}>
              <thead><tr>{["Quarter","Income","Expenses","Net Profit","Est. Tax (25%)"].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
              <tbody>
                {quarters.map(q=>(
                  <tr key={q.q} style={C.tr}>
                    <td style={{...C.td,fontWeight:700}}>{q.q} {year}</td>
                    <td style={{...C.td,color:"#2A9D8F",fontWeight:600}}>{$(q.qInc)}</td>
                    <td style={{...C.td,color:"#E76F51",fontWeight:600}}>{$(q.qExp)}</td>
                    <td style={{...C.td,fontWeight:700,color:q.qNet>=0?T.green:"#f87171"}}>{$(q.qNet)}</td>
                    <td style={{...C.td,fontWeight:800,color:"#b8860b"}}>{$(q.estTax)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{background:"#eef7f6"}}>
                  <td style={{...C.td,fontWeight:700}}>FULL YEAR {year}</td>
                  <td style={{...C.td,fontWeight:800,color:"#2A9D8F"}}>{$(income)}</td>
                  <td style={{...C.td,fontWeight:800,color:"#E76F51"}}>{$(totalExp)}</td>
                  <td style={{...C.td,fontWeight:800,color:netProfit>=0?T.green:"#f87171"}}>{$(netProfit)}</td>
                  <td style={{...C.td,fontWeight:800,color:"#b8860b"}}>{$(quarters.reduce((s,q)=>s+q.estTax,0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHOP / PACKAGES PAGE ─────────────────────────────────────────────────────
function ShopPage({packages, onNew, onEdit}) {
  const tiers = [
    {id:"starter", label:"Starter",     color:T.amber, bg:"#fefbf0"},
    {id:"standard",label:"Standard",    color:"#2A9D8F", bg:"#f0faf9"},
    {id:"premium", label:"Premium",     color:"#E76F51", bg:"#fff4f1"},
    {id:"custom",  label:"Custom",      color:"#a78bfa", bg:"#f5f3ff"},
  ];

  return (
    <div>
      <PH title="Shop / Packages" sub={`${packages.length} pricing tiers`} action={{lbl:"+ New Package",fn:onNew}}/>

      <div style={{...C.card,padding:"12px 16px",marginBottom:20,background:"#f0faf9",border:`1px solid #2A9D8F30`}}>
        <div style={{fontSize:12,fontWeight:700,color:"#2A9D8F",marginBottom:2}}>💡 Tip</div>
        <div style={{fontSize:11,color:T.mid}}>These packages can be shared with clients and linked to invoices. Use them as your pricing menu. / Estos paquetes se pueden compartir con clientes y vincular a facturas.</div>
      </div>

      {/* Tier groups */}
      {tiers.map(tier=>{
        const pkgs = packages.filter(p=>p.tier===tier.id);
        if (!pkgs.length) return null;
        return (
          <div key={tier.id} style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <span style={{width:12,height:12,borderRadius:"50%",background:tier.color,display:"inline-block"}}/>
              <span style={{fontWeight:700,fontSize:14,color:tier.color}}>{tier.label} Tier</span>
              <div style={{flex:1,height:1,background:"#e8e0d4",marginLeft:4}}/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
              {pkgs.map(pkg=>(
                <div key={pkg.id} style={{...C.card,overflow:"hidden",position:"relative"}}>
                  {pkg.popular&&<div style={{position:"absolute",top:0,right:0,background:"#E76F51",color:"#fff",fontSize:9,fontWeight:700,padding:"3px 10px",borderRadius:"0 10px 0 8px",letterSpacing:.5}}>POPULAR</div>}
                  {/* Color header */}
                  <div style={{height:6,background:pkg.color||tier.color}}/>
                  <div style={{padding:"16px"}}>
                    <div style={{fontWeight:800,fontSize:16,color:T.dark,marginBottom:4}}>{pkg.name}</div>
                    <div style={{fontSize:26,fontWeight:800,color:pkg.color||tier.color,marginBottom:8}}>${Number(pkg.price||0).toFixed(0)}<span style={{fontSize:12,fontWeight:400,color:T.mid}}>/event</span></div>
                    <div style={{fontSize:12,color:"#666",marginBottom:12,lineHeight:1.4}}>{pkg.description}</div>
                    <div style={{borderTop:`1px solid #f0e8e0`,paddingTop:10}}>
                      {(pkg.includes||[]).map((item,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:7,marginBottom:5,fontSize:11,color:"#555"}}>
                          <span style={{color:pkg.color||tier.color,flexShrink:0,marginTop:1}}>✓</span>{item}
                        </div>
                      ))}
                    </div>
                    <button style={{...C.editBtn,marginTop:12,width:"100%",justifyContent:"center",display:"flex"}} onClick={()=>onEdit(pkg)}>
                      Edit Package
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {!packages.length && <ES emoji="🛍️" title="No packages yet" sub="Add your first pricing tier"/>}
    </div>
  );
}

// ─── PACKAGE MODAL ────────────────────────────────────────────────────────────
function PkgMod({data, onSave, onDel, onX}) {
  const [f, sf] = useState(data || {name:"", tier:"starter", price:"", description:"", includes:[""], color:"#2A9D8F", popular:false});
  const s = (k,v) => sf(x=>({...x,[k]:v}));

  const updateItem = (i,v) => sf(x=>({...x,includes:x.includes.map((it,idx)=>idx===i?v:it)}));
  const addItem    = ()    => sf(x=>({...x,includes:[...x.includes,""]}));
  const removeItem = i    => sf(x=>({...x,includes:x.includes.filter((_,idx)=>idx!==i)}));

  return (
    <MW title={data?"Edit Package":"New Package"} onX={onX}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <FL lbl="Package Name"><input style={C.inp} value={f.name} onChange={e=>s("name",e.target.value)} placeholder="e.g. Garden Party"/></FL>
        <FL lbl="Tier">
          <select style={C.inp} value={f.tier} onChange={e=>s("tier",e.target.value)}>
            {["starter","standard","premium","custom"].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
        </FL>
        <FL lbl="Price ($)"><input type="number" style={C.inp} value={f.price} onChange={e=>s("price",e.target.value)} placeholder="499"/></FL>
        <FL lbl="Accent Color">
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input type="color" value={f.color} onChange={e=>s("color",e.target.value)} style={{width:36,height:36,borderRadius:6,border:`1px solid #d8d0c4`,cursor:"pointer",padding:2}}/>
            <input style={{...C.inp,flex:1}} value={f.color} onChange={e=>s("color",e.target.value)} placeholder="#2A9D8F"/>
          </div>
        </FL>
      </div>
      <FL lbl="Description">
        <textarea style={{...C.inp,height:56}} value={f.description} onChange={e=>s("description",e.target.value)} placeholder="Brief description for clients…"/>
      </FL>
      <FL lbl="What's Included">
        {f.includes.map((item,i)=>(
          <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
            <input style={{...C.inp,flex:1}} value={item} onChange={e=>updateItem(i,e.target.value)} placeholder={`Item ${i+1}`}/>
            {f.includes.length>1&&<button style={{background:"none",border:"none",cursor:"pointer",color:"#f87171",padding:2,display:"flex"}} onClick={()=>removeItem(i)}><IconTrash/></button>}
          </div>
        ))}
        <button style={{...C.editBtn,fontSize:11}} onClick={addItem}>+ Add item</button>
      </FL>
      <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,cursor:"pointer",color:"#6b4060",marginBottom:4}}>
        <input type="checkbox" checked={f.popular} onChange={e=>s("popular",e.target.checked)}/>
        Mark as Popular / Marcar como Popular
      </label>
      <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
    </MW>
  );
}

// ─── CONTRACTS PAGE ───────────────────────────────────────────────────────────
function ContractsPage({contracts,clients,bookings,invoices,onNew,onEdit,onView,onStatus}) {
  const [filter,setFilter] = useState("all");
  const shown = filter==="all" ? contracts : contracts.filter(c=>c.status===filter);
  const sorted = [...shown].sort((a,b)=>(b.createdAt||0)-(a.createdAt||0));

  return (
    <div>
      <PH title="Contracts" sub={`${contracts.length} total contracts`} action={{lbl:"+ New Contract",fn:onNew}}/>

      {/* Stats row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {Object.entries(CONTRACT_STATUS).map(([k,v])=>{
          const cnt = contracts.filter(c=>c.status===k).length;
          return (
            <div key={k} style={{...C.card,borderTop:`3px solid ${v.color}`,padding:"12px 14px",cursor:"pointer"}} onClick={()=>setFilter(filter===k?"all":k)}>
              <div style={{fontSize:22,fontWeight:800,color:v.color}}>{cnt}</div>
              <div style={{fontSize:12,fontWeight:600,marginTop:1}}>{v.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filter pills */}
      <div style={C.filters}>
        <button style={{...C.fBtn,...(filter==="all"?C.fOn:{})}} onClick={()=>setFilter("all")}>All Contracts</button>
        {Object.entries(CONTRACT_STATUS).map(([k,v])=>(
          <button key={k} style={{...C.fBtn,...(filter===k?{background:v.color,color:"#fff"}:{})}} onClick={()=>setFilter(k)}>{v.label}</button>
        ))}
      </div>

      {/* Contracts list */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {sorted.map(con=>{
          const client  = clients.find(c=>c.id===con.clientId);
          const booking = bookings.find(b=>b.id===con.bookingId);
          const invoice = invoices.find(v=>v.id===con.invoiceId);
          const cfg     = CONTRACT_STATUS[con.status]||CONTRACT_STATUS.draft;
          return (
            <div key={con.id} style={{...C.card,padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15,color:T.dark,marginBottom:3}}>{con.title||"Untitled Contract"}</div>
                  <div style={{fontSize:12,color:T.mid}}>
                    <b style={{color:"#2A9D8F"}}>{client?.name||con.clientName||"—"}</b>
                    {con.eventDate && <span> · 📅 {con.eventDate}</span>}
                    {con.eventType && <span> · {con.eventType}</span>}
                  </div>
                </div>
                <span style={{...BK.stagePill,background:cfg.bg,color:cfg.color,fontSize:11}}>{cfg.label}</span>
              </div>

              {/* Linked badges */}
              <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                {booking && <span style={{fontSize:10,background:"#f0faf9",color:"#2A9D8F",padding:"2px 8px",borderRadius:12,border:`1px solid #b0ddd9`}}>📋 {booking.name}</span>}
                {invoice && <span style={{fontSize:10,background:T.cream,color:"#E76F51",padding:"2px 8px",borderRadius:12,border:`1px solid #f0c8b0`}}>🧾 {invoice.invoiceNumber}</span>}
                {con.totalValue>0 && <span style={{fontSize:10,background:"#f0fdf4",color:T.green,padding:"2px 8px",borderRadius:12,border:`1px solid #a7f3d0`,fontWeight:700}}>${Number(con.totalValue).toFixed(2)}</span>}
              </div>

              {/* Status timeline */}
              <div style={{display:"flex",gap:4,marginBottom:12,alignItems:"center"}}>
                {["draft","sent","signed"].map((s,i)=>{
                  const scfg = CONTRACT_STATUS[s];
                  const done = ["draft","sent","signed"].indexOf(con.status) >= i;
                  return (
                    <div key={s} style={{display:"flex",alignItems:"center",gap:4}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:done?scfg.color:"#e8e0d4",transition:"background .3s"}}/>
                      <span style={{fontSize:9,color:done?scfg.color:"#bbb",fontWeight:done?700:400}}>{scfg.label}</span>
                      {i<2 && <div style={{width:16,height:1,background:done&&["sent","signed"].indexOf(con.status)>i?"#2A9D8F":"#e8e0d4"}}/>}
                    </div>
                  );
                })}
                {con.status==="void" && <span style={{fontSize:10,color:"#f87171",fontWeight:700,marginLeft:4}}>⚠ Void</span>}
              </div>

              {/* Signature info */}
              {con.status==="signed" && (
                <div style={{fontSize:11,color:T.green,background:"#f0fdf4",padding:"5px 10px",borderRadius:6,marginBottom:8}}>
                  ✅ Signed{con.signedDate?` on ${con.signedDate}`:""}
                  {con.signedBy && ` by ${con.signedBy}`}
                </div>
              )}
              {con.status==="sent" && con.sentDate && (
                <div style={{fontSize:11,color:T.blue,background:"#eff6ff",padding:"5px 10px",borderRadius:6,marginBottom:8}}>
                  📤 Sent on {con.sentDate}
                </div>
              )}

              {/* Actions */}
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button style={C.iconBtn} onClick={()=>onView(con.id)} title="Preview"><IconEye/></button>
                <button style={C.editBtn} onClick={()=>onEdit(con)}>Edit</button>
                {con.status==="draft" && <button style={{...C.tealBtn,fontSize:11,padding:"4px 12px"}} onClick={()=>onStatus(con.id,"sent",{sentDate:new Date().toISOString().slice(0,10)})}>Mark Sent</button>}
                {con.status==="sent"  && <button style={{...C.tealBtn,fontSize:11,padding:"4px 12px",background:T.green}} onClick={()=>onStatus(con.id,"signed",{signedDate:new Date().toISOString().slice(0,10)})}>Mark Signed ✓</button>}
                {con.status!=="void" && con.status!=="signed" && <button style={{...C.editBtn,color:"#f87171",borderColor:"#f87171",fontSize:11}} onClick={()=>onStatus(con.id,"void")}>Void</button>}
              </div>
            </div>
          );
        })}
        {!sorted.length && <ES emoji="📝" title="No contracts yet" sub="Sin contratos aún"/>}
      </div>
    </div>
  );
}

// ─── CONTRACT MODAL (create/edit) ─────────────────────────────────────────────
function ContractMod({data,onSave,onDel,onX,clients,bookings,invoices}) {
  const [f,sf] = useState(data || {
    title:"", clientId:"", clientName:"", bookingId:"", invoiceId:"",
    eventType:"", eventDate:"", venue:"", totalValue:"",
    deliverables:"",
    terms: DEFAULT_CONTRACT_TERMS,
    status:"draft", notes:"",
  });
  const s = (k,v) => sf(x=>({...x,[k]:v}));

  // Auto-fill from booking
  const fillFromBooking = id => {
    const b = bookings.find(x=>x.id===id);
    if (!b) return;
    sf(x=>({...x,
      bookingId: id,
      clientName: b.name,
      eventType: b.eventType,
      eventDate: b.eventDate,
      venue: b.venue,
      title: x.title || `${b.eventType} — ${b.name}`,
    }));
  };

  // Auto-fill from invoice — pulls line items as deliverables
  const fillFromInvoice = id => {
    const v = invoices.find(x=>x.id===id);
    if (!v) return;
    const cl = clients.find(c=>String(c.id)===String(v.clientId));
    // Build deliverables text from line items
    const lines = (v.lineItems||[]).map(li => {
      const qty   = Number(li.qty||1);
      const price = Number(li.price||0);
      const base  = `• ${li.name}${qty>1?` (x${qty})`:""} — $${(price*qty).toFixed(2)}`;
      // If package, append includes indented
      const includes = (li.includes||[]).map(item=>`  ✓ ${item}`).join("\n");
      return includes ? `${base}\n${includes}` : base;
    });
    const total = `\nTotal: $${Number(v.total||0).toFixed(2)}`;
    const dep   = v.depositPaid>0 ? `\nDeposit Paid: $${Number(v.depositPaid||0).toFixed(2)}` : "";
    const bal   = `\nBalance Due: $${Number(v.balanceDue||0).toFixed(2)}`;
    const delivText = lines.join("\n") + "\n" + "─".repeat(30) + total + dep + bal;
    sf(x=>({...x,
      invoiceId:    id,
      clientId:     v.clientId,
      clientName:   cl?.name||v.clientName||x.clientName,
      totalValue:   v.total||x.totalValue,
      deliverables: x.deliverables || delivText,
      title:        x.title || (cl?.name ? `Service Agreement — ${cl.name}` : x.title),
    }));
  };

  const selClient = clients.find(c=>String(c.id)===String(f.clientId));

  return (
    <MW title={data?"Edit Contract":"New Contract"} onX={onX}>
      <FL lbl="Contract Title">
        <input style={C.inp} value={f.title} onChange={e=>s("title",e.target.value)} placeholder="e.g. Baby Shower — Ashley T."/>
      </FL>

      {/* Auto-fill sources */}
      <div style={{background:"#f0faf9",borderRadius:8,padding:"10px 12px",marginBottom:12}}>
        <div style={{fontSize:10,fontWeight:700,color:"#2A9D8F",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Auto-fill from · Rellenar desde</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <FL lbl="From Booking">
            <select style={C.inp} value={f.bookingId} onChange={e=>fillFromBooking(e.target.value)}>
              <option value="">— Select booking —</option>
              {bookings.map(b=><option key={b.id} value={b.id}>{b.name} · {b.eventType}</option>)}
            </select>
          </FL>
          <FL lbl="From Invoice">
            <select style={C.inp} value={f.invoiceId} onChange={e=>fillFromInvoice(e.target.value)}>
              <option value="">— Select invoice —</option>
              {invoices.map(v=><option key={v.id} value={v.id}>{v.invoiceNumber} · {v.clientName||"—"}</option>)}
            </select>
          </FL>
        </div>
      </div>

      {/* Client details */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <FL lbl="Client / Cliente">
          <select style={C.inp} value={f.clientId} onChange={e=>{
            const cl=clients.find(c=>String(c.id)===String(e.target.value));
            sf(x=>({...x,clientId:e.target.value,clientName:cl?.name||""}));
          }}>
            <option value="">— Select client —</option>
            {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FL>
        <FL lbl="Client Name (override)">
          <input style={C.inp} value={f.clientName} onChange={e=>s("clientName",e.target.value)} placeholder={selClient?.name||"Name on contract"}/>
        </FL>
        <FL lbl="Event Type">
          <select style={C.inp} value={f.eventType} onChange={e=>s("eventType",e.target.value)}>
            <option value="">— Type —</option>
            {BOOK_EVENT_TYPES.map(t=><option key={t}>{t}</option>)}
          </select>
        </FL>
        <FL lbl="Event Date"><input type="date" style={C.inp} value={f.eventDate} onChange={e=>s("eventDate",e.target.value)}/></FL>
        <FL lbl="Venue / Location"><input style={C.inp} value={f.venue} onChange={e=>s("venue",e.target.value)}/></FL>
        <FL lbl="Total Value ($)"><input type="number" style={C.inp} value={f.totalValue} onChange={e=>s("totalValue",e.target.value)} placeholder="0.00"/></FL>
      </div>

      <FL lbl="Deliverables / Servicios a Entregar">
        <div style={{marginBottom:5,fontSize:10,color:T.mid}}>
          Auto-populated from invoice line items. Edit freely — this appears on the contract above the terms.
        </div>
        <textarea
          style={{...C.inp,height:140,fontSize:11,lineHeight:1.7,fontFamily:T.fontDisplay}}
          value={f.deliverables}
          onChange={e=>s("deliverables",e.target.value)}
          placeholder={"• Balloon Arch (Full) — $180.00\n• Setup & Breakdown — $200.00\n──────────────────────────\nTotal: $380.00\nDeposit Paid: $190.00\nBalance Due: $190.00"}
        />
      </FL>

      <FL lbl="Contract Terms (editable) / Términos del Contrato">
        <textarea style={{...C.inp,height:180,fontSize:11,lineHeight:1.6,fontFamily:T.fontDisplay}} value={f.terms} onChange={e=>s("terms",e.target.value)}/>
      </FL>
      <button style={{...C.editBtn,fontSize:11,marginBottom:10}} onClick={()=>s("terms",DEFAULT_CONTRACT_TERMS)}>↺ Reset to default terms</button>

      <FL lbl="Notes / Notas (appear on contract)">
        <textarea style={{...C.inp,height:72,whiteSpace:"pre-wrap",lineHeight:1.5}} value={f.notes} onChange={e=>s("notes",e.target.value)} placeholder={"Any additional notes for the client.\nLine breaks are preserved."}/>
      </FL>

      <MA onSave={()=>onSave(f)} onDel={data?()=>onDel(data.id):null} onX={onX}/>
    </MW>
  );
}

// ─── CONTRACT PRINT ───────────────────────────────────────────────────────────
function printContract(contract) {
  const fmt = n => n ? `$${Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",")}` : "—";
  const created = new Date(contract.createdAt||Date.now()).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});

  const infoRows = [
    ["Client / Cliente",    contract.clientName||"—"],
    ["Event Type",          contract.eventType||"—"],
    ["Event Date / Fecha",  contract.eventDate||"—"],
    ["Venue / Lugar",       contract.venue||"—"],
    ["Contract Title",      contract.title||"—"],
    ["Total Value / Monto", fmt(contract.totalValue)],
  ].map(([l,v])=>`
    <div style="background:#f7f3ec;border-radius:7px;padding:10px 13px;">
      <div style="font-size:9px;font-weight:700;color:#2A9D8F;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px;">${l}</div>
      <div style="font-weight:600;font-size:13px;color:#1a2e2b;">${v}</div>
    </div>`).join("");

  const sigBlock = contract.status==="signed"
    ? `<div style="font-size:22px;font-family:cursive;color:#2A9D8F;font-style:italic;padding:8px 0;">${contract.signedBy||contract.clientName||""}</div>
       <div style="font-size:10px;color:#7ab8b4;margin-top:2px;">Signed: ${contract.signedDate||""} &nbsp;·&nbsp; ${contract.signedBy||""}</div>`
    : `<div style="height:56px;"></div><div style="font-size:10px;color:#aaa;font-style:italic;">Awaiting signature…</div>`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Contract — ${contract.clientName||"Bazani Creations"}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#1a2e2b;padding:32px 40px;max-width:740px;margin:0 auto;}
  @media print{body{padding:16px 20px;}@page{margin:0.6in;}}
  @media(max-width:600px){body{padding:16px;}}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #eef7f6;padding-bottom:18px;margin-bottom:20px;flex-wrap:wrap;gap:12px;}
  .brand{font-size:22px;font-weight:800;color:#2A9D8F;letter-spacing:.5px;}
  .brand-sub{font-size:11px;color:#7ab8b4;margin-top:2px;}
  .doc-type{font-size:14px;font-weight:700;text-align:right;color:#1a2e2b;}
  .doc-meta{font-size:11px;color:#7ab8b4;text-align:right;margin-top:4px;}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:20px;}
  .section-label{font-size:10px;font-weight:700;color:#1a2e2b;text-transform:uppercase;letter-spacing:.6px;border-bottom:1px solid #eef7f6;padding-bottom:6px;margin-bottom:10px;}
  .text-block{white-space:pre-wrap;word-break:break-word;font-size:12px;color:#1a2e2b;line-height:1.8;background:#f7f3ec;border-radius:7px;padding:12px 16px;margin-bottom:20px;}
  .terms-block{white-space:pre-wrap;word-break:break-word;font-size:10px;color:#444;line-height:1.8;margin-bottom:20px;}
  .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:8px;}
  .sig-box{border-bottom:2px solid #1a2e2b;min-height:64px;padding-bottom:6px;margin-bottom:6px;}
  .sig-label{font-size:10px;color:#7ab8b4;}
  .no-print{margin-top:28px;text-align:center;}
  .print-btn{background:#2A9D8F;color:#fff;border:none;padding:12px 32px;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer;}
  @media print{.no-print{display:none;}}
</style>
</head>
<body>

<div class="header">
  <div>
    <div class="brand">Bazani Creations</div>
    <div class="brand-sub">Custom Event Decor · Columbus, Ohio</div>
  </div>
  <div>
    <div class="doc-type">SERVICE CONTRACT</div>
    <div class="doc-meta">Created: ${created}</div>
  </div>
</div>

<div class="info-grid">${infoRows}</div>

${contract.deliverables ? `
<div class="section-label">Scope of Services / Servicios Acordados</div>
<div class="text-block">${contract.deliverables}</div>` : ""}

${contract.notes && contract.notes.trim() ? `
<div class="section-label">Notes / Notas</div>
<div class="text-block">${contract.notes}</div>` : ""}

<div class="section-label">Contract Terms / Términos del Contrato</div>
<div class="terms-block">${(contract.terms||DEFAULT_CONTRACT_TERMS).replace(/</g,"&lt;")}</div>

<div class="section-label">Signatures / Firmas</div>
<div class="sig-grid">
  <div>
    <div class="sig-box">${sigBlock}</div>
    <div class="sig-label">Client Signature / Firma del Cliente</div>
  </div>
  <div>
    <div class="sig-box" style="height:64px;"></div>
    <div class="sig-label">Bazani Creations Representative</div>
    <div class="sig-label" style="margin-top:2px;">Date: _______________</div>
  </div>
</div>

<div class="no-print">
  <button class="print-btn" onclick="window.print()">📄 Save as PDF / Print</button>
</div>
<script>
  if(!/iPhone|iPad|iPod/.test(navigator.userAgent)){
    setTimeout(()=>window.print(),400);
  }
<\/script>
</body></html>`;

  const w = window.open("","_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

// ─── CONTRACT VIEW ────────────────────────────────────────────────────────────
function ContractView({contract,onX,onStatus}) {
  const cfg = CONTRACT_STATUS[contract.status]||CONTRACT_STATUS.draft;
  const [signing,   setSigning]   = useState(false);
  const [sigName,   setSigName]   = useState(contract.clientName||"");
  const [sigDate,   setSigDate]   = useState(new Date().toISOString().slice(0,10));
  const canvasRef = useRef(null);
  const [drawing,   setDrawing]   = useState(false);
  const [hasDraw,   setHasDraw]   = useState(false);
  const [sigMode,   setSigMode]   = useState("type"); // type | draw

  if (!contract.id) return null;

  const startDraw = e => {
    const c = canvasRef.current; if (!c) return;
    setDrawing(true); setHasDraw(true);
    const r = c.getBoundingClientRect();
    const cx = e.clientX||e.touches?.[0]?.clientX; const cy = e.clientY||e.touches?.[0]?.clientY;
    const ctx = c.getContext("2d");
    ctx.beginPath(); ctx.moveTo(cx-r.left, cy-r.top);
  };
  const draw = e => {
    if (!drawing) return;
    const c = canvasRef.current; if (!c) return;
    const r = c.getBoundingClientRect();
    const cx = e.clientX||e.touches?.[0]?.clientX; const cy = e.clientY||e.touches?.[0]?.clientY;
    const ctx = c.getContext("2d");
    ctx.lineTo(cx-r.left, cy-r.top);
    ctx.strokeStyle = T.dark; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.stroke();
  };
  const endDraw = () => setDrawing(false);
  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0,0,c.width,c.height);
    setHasDraw(false);
  };

  const doSign = () => {
    if (!sigName.trim()) { alert("Please enter a name to sign."); return; }
    let sigData = sigName.trim();
    if (sigMode === "draw" && canvasRef.current && hasDraw) {
      sigData = canvasRef.current.toDataURL();
    }
    onStatus(contract.id, "signed", {
      signedDate: sigDate,
      signedBy: sigName.trim(),
      signatureData: sigData,
    });
    setSigning(false);
    onX();
  };

  return (
    <div style={C.overlay} onClick={signing ? undefined : onX}>
      <div style={{...C.mbox,maxWidth:680,width:"95%"}} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{...C.mhead,background:T.dark}}>
          <span style={{fontWeight:700,fontSize:14,color:"#f8f4ee"}}>Contract Preview</span>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button style={{...C.tealBtn,fontSize:11,padding:"4px 12px",background:"#475569"}} onClick={()=>printContract(contract)}>📄 PDF / Share</button>
            {contract.status==="draft" && <button style={{...C.tealBtn,fontSize:11,padding:"4px 12px"}} onClick={()=>{onStatus(contract.id,"sent",{sentDate:new Date().toISOString().slice(0,10)});onX();}}>Mark Sent</button>}
            {contract.status==="sent"  && !signing && <button style={{...C.tealBtn,fontSize:11,padding:"4px 12px",background:T.green}} onClick={()=>setSigning(true)}>✍ Sign Contract</button>}
            <button style={{...C.xBtn,color:T.light}} onClick={onX}><IconX/></button>
          </div>
        </div>

        {/* Signature capture panel */}
        {signing && (
          <div style={{background:"#f0fdf4",border:`2px solid ${T.green}`,borderRadius:8,margin:"16px 20px 0",padding:"16px"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1a4a2a",marginBottom:12}}>✍ Sign Contract — {contract.clientName||"Client"}</div>
            <div style={{display:"flex",gap:8,marginBottom:10}}>
              {["type","draw"].map(m=>(
                <button key={m} onClick={()=>setSigMode(m)}
                  style={{padding:"5px 14px",borderRadius:18,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,
                    background:sigMode===m?"#2A9D8F":"#e8f5e9",color:sigMode===m?"#fff":"#2A9D8F"}}>
                  {m==="type"?"⌨ Type Name":"✏ Draw"}
                </button>
              ))}
            </div>
            {sigMode==="type" ? (
              <input style={{...C.inp,fontSize:20,fontFamily:"cursive",color:T.dark,marginBottom:8}}
                placeholder="Type full name to sign…" value={sigName} onChange={e=>setSigName(e.target.value)}/>
            ) : (
              <div style={{marginBottom:8}}>
                <canvas ref={canvasRef} width={400} height={80}
                  style={{border:`2px solid #2A9D8F`,borderRadius:8,background:"#fff",width:"100%",touchAction:"none",cursor:"crosshair"}}
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                  onTouchStart={e=>{e.preventDefault();startDraw(e);}} onTouchMove={e=>{e.preventDefault();draw(e);}} onTouchEnd={endDraw}
                />
                <button style={{...C.editBtn,fontSize:10,marginTop:4}} onClick={clearCanvas}>Clear</button>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
              <FL lbl="Signee Name"><input style={C.inp} value={sigName} onChange={e=>setSigName(e.target.value)} placeholder="Full legal name"/></FL>
              <FL lbl="Date"><input type="date" style={C.inp} value={sigDate} onChange={e=>setSigDate(e.target.value)}/></FL>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button style={{...C.tealBtn,background:T.green,flex:1,justifyContent:"center"}} onClick={doSign}>✅ Confirm Signature</button>
              <button style={C.cancelBtn} onClick={()=>setSigning(false)}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{padding:"24px 28px",maxHeight:"72vh",overflowY:"auto",fontSize:13}}>
          {/* Bazani header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,paddingBottom:16,borderBottom:`2px solid #eef7f6`}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <img src={APP_LOGO} alt="Bazani Creations" style={{height:40,width:"auto",objectFit:"contain"}}/>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:"#2A9D8F"}}>Bazani Creations</div>
                <div style={{fontSize:10,color:T.mid}}>Custom Event Decor · Columbus, Ohio</div>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:13,fontWeight:700,color:T.dark}}>SERVICE CONTRACT</div>
              <div style={{marginTop:4}}><Bdg cfg={cfg}>{cfg.label}</Bdg></div>
              <div style={{fontSize:10,color:T.mid,marginTop:4}}>Created: {new Date(contract.createdAt||Date.now()).toLocaleDateString()}</div>
            </div>
          </div>

          {/* Event & Client details */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
            {[
              {l:"Client / Cliente",      v: contract.clientName||"—"},
              {l:"Event Type",            v: contract.eventType||"—"},
              {l:"Event Date / Fecha",    v: contract.eventDate||"—"},
              {l:"Venue / Lugar",         v: contract.venue||"—"},
              {l:"Contract Title",        v: contract.title||"—"},
              {l:"Total Value / Monto",   v: contract.totalValue ? `$${Number(contract.totalValue).toFixed(2)}` : "—"},
            ].map(r=>(
              <div key={r.l} style={{background:T.cream,borderRadius:7,padding:"8px 11px"}}>
                <div style={{fontSize:9,fontWeight:700,color:"#2A9D8F",textTransform:"uppercase",letterSpacing:.5,marginBottom:2}}>{r.l}</div>
                <div style={{fontWeight:600,fontSize:13,color:T.dark}}>{r.v}</div>
              </div>
            ))}
          </div>

          {/* Deliverables */}
          {contract.deliverables && (
            <div style={{marginBottom:18}}>
              <div style={{fontSize:11,fontWeight:700,color:T.dark,textTransform:"uppercase",letterSpacing:.6,marginBottom:10,borderBottom:`1px solid #eef7f6`,paddingBottom:6}}>
                Scope of Services / Servicios Acordados
              </div>
              <pre style={{whiteSpace:"pre-wrap",wordBreak:"break-word",fontFamily:T.fontDisplay,fontSize:12,color:T.dark,lineHeight:1.8,margin:0,background:T.cream,borderRadius:7,padding:"10px 14px"}}>{contract.deliverables}</pre>
            </div>
          )}

          {/* Notes */}
          {contract.notes && contract.notes.trim() && (
            <div style={{marginBottom:18}}>
              <div style={{fontSize:11,fontWeight:700,color:T.dark,textTransform:"uppercase",letterSpacing:.6,marginBottom:10,borderBottom:`1px solid #eef7f6`,paddingBottom:6}}>
                Notes / Notas
              </div>
              <div style={{whiteSpace:"pre-wrap",wordBreak:"break-word",fontFamily:T.fontDisplay,fontSize:12,color:T.dark,lineHeight:1.7,background:T.cream,borderRadius:7,padding:"10px 14px"}}>{contract.notes}</div>
            </div>
          )}

          {/* Contract terms */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:T.dark,textTransform:"uppercase",letterSpacing:.6,marginBottom:10,borderBottom:`1px solid #eef7f6`,paddingBottom:6}}>Contract Terms / Términos del Contrato</div>
            <pre style={{whiteSpace:"pre-wrap",wordBreak:"break-word",fontFamily:T.fontDisplay,fontSize:11,color:"#333",lineHeight:1.8,margin:0}}>{contract.terms||DEFAULT_CONTRACT_TERMS}</pre>
          </div>

          {/* Signature block */}
          <div style={{borderTop:`2px solid #eef7f6`,paddingTop:16,marginTop:8}}>
            <div style={{fontSize:11,fontWeight:700,color:T.dark,textTransform:"uppercase",letterSpacing:.6,marginBottom:14}}>Signatures / Firmas</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
              {/* Client signature */}
              <div>
                <div style={{borderBottom:`2px solid ${T.dark}`,height:56,marginBottom:6,position:"relative",borderRadius:"4px 4px 0 0",background:T.cream,display:"flex",alignItems:"flex-end",padding:"4px 8px"}}>
                  {contract.status==="signed" && (
                    contract.signatureData && contract.signatureData.startsWith("data:") ? (
                      <img src={contract.signatureData} alt="signature" style={{maxHeight:48,maxWidth:"100%",objectFit:"contain"}}/>
                    ) : (
                      <div style={{fontSize:22,fontFamily:"cursive",color:"#2A9D8F",fontStyle:"italic"}}>{contract.signedBy||contract.clientName}</div>
                    )
                  )}
                  {contract.status!=="signed" && <div style={{color:"#ccc",fontSize:11,fontStyle:"italic"}}>Pending signature…</div>}
                </div>
                <div style={{fontSize:10,color:T.mid}}>Client Signature / Firma del Cliente</div>
                <div style={{fontSize:10,color:T.mid,marginTop:2}}>
                  Name: {contract.status==="signed" ? contract.signedBy||contract.clientName||"—" : "___________"}
                </div>
                <div style={{fontSize:10,color:T.mid,marginTop:2}}>
                  Date: {contract.status==="signed" ? contract.signedDate||"___________" : "___________"}
                </div>
              </div>
              {/* Bazani signature */}
              <div>
                <div style={{borderBottom:`2px solid ${T.dark}`,height:56,marginBottom:6,position:"relative",borderRadius:"4px 4px 0 0",background:T.cream,display:"flex",alignItems:"flex-end",padding:"4px 8px"}}>
                  {contract.status==="signed" && (
                    <div style={{fontSize:22,fontFamily:"cursive",color:"#E76F51",fontStyle:"italic"}}>Bazani Creations</div>
                  )}
                </div>
                <div style={{fontSize:10,color:T.mid}}>Bazani Creations Representative</div>
                <div style={{fontSize:10,color:T.mid,marginTop:2}}>Date: {contract.status==="signed" ? contract.signedDate||"___________" : "___________"}</div>
              </div>
            </div>
          </div>

          {/* Status banner */}
          {contract.status==="signed" && (
            <div style={{marginTop:16,background:"#f0fdf4",border:`1px solid #a7f3d0`,borderRadius:8,padding:"10px 14px",textAlign:"center",fontSize:12,color:T.green,fontWeight:700}}>
              ✅ This contract was signed on {contract.signedDate||"—"}
              {contract.signedBy ? ` by ${contract.signedBy}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN (Supabase Auth) ────────────────────────────────────────────
function LoginScreen({loading}) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [busy,     setBusy]     = useState(false);

  const handleLogin = async e => {
    e.preventDefault();
    if (!email || !password) { setErr("Please enter your email and password."); return; }
    setBusy(true); setErr("");
    try {
      await signIn(email.trim(), password);
      // onAuthStateChange in App will pick up the new session automatically
    } catch (e) {
      setErr(e.message || "Login failed. Check your email and password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:T.dark,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:T.fontUI}}>
      <img src={APP_LOGO} alt="Bazani Creations" style={{height:90,width:"auto",objectFit:"contain",marginBottom:12}}/>
      <div style={{fontFamily:T.fontDisplay,fontSize:22,fontWeight:700,color:"#f8f4ee",marginBottom:4,letterSpacing:"-.3px"}}>Bazani Creations</div>
      <div style={{fontSize:12,color:"#6ea89f",marginBottom:36,letterSpacing:.5}}>Event Decor CRM</div>

      {loading ? (
        <div style={{color:"#6ea89f",fontSize:13}}>Loading…</div>
      ) : (
        <form onSubmit={handleLogin} style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#6ea89f",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Email</div>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={e=>setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1.5px solid #2e5a56",background:"#1a3532",color:"#f8f4ee",fontSize:14,fontFamily:T.fontUI,outline:"none",boxSizing:"border-box"}}
            />
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#6ea89f",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Password</div>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e=>setPassword(e.target.value)}
              placeholder="••••••••"
              style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1.5px solid #2e5a56",background:"#1a3532",color:"#f8f4ee",fontSize:14,fontFamily:T.fontUI,outline:"none",boxSizing:"border-box"}}
            />
          </div>
          {err && (
            <div style={{background:"#3d1a1a",border:"1px solid #f87171",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#fca5a5"}}>
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{background:T.teal,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontWeight:700,fontSize:14,cursor:busy?"not-allowed":"pointer",fontFamily:T.fontUI,opacity:busy?.7:1,marginTop:4}}>
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <div style={{textAlign:"center",fontSize:11,color:"#4a7a72",marginTop:4,lineHeight:1.6}}>
            Access is invite-only. Contact your admin to get access.
          </div>
        </form>
      )}
    </div>
  );
}

// ─── SET NEW PASSWORD SCREEN (after password-reset link) ─────────────────────
function SetNewPasswordScreen({onDone}) {
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [err,       setErr]       = useState("");
  const [busy,      setBusy]      = useState(false);
  const [done,      setDone]      = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      setTimeout(() => onDone(), 1800);
    } catch (e) {
      setErr(e.message || "Failed to update password. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:T.dark,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:T.fontUI}}>
      <img src={APP_LOGO} alt="Bazani Creations" style={{height:90,width:"auto",objectFit:"contain",marginBottom:12}}/>
      <div style={{fontFamily:T.fontDisplay,fontSize:22,fontWeight:700,color:"#f8f4ee",marginBottom:4,letterSpacing:"-.3px"}}>Set New Password</div>
      <div style={{fontSize:12,color:"#6ea89f",marginBottom:36,letterSpacing:.5}}>Choose a new password for your account</div>

      {done ? (
        <div style={{color:"#6ea89f",fontSize:14,textAlign:"center"}}>✓ Password updated! Taking you in…</div>
      ) : (
        <form onSubmit={handleSubmit} style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#6ea89f",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>New Password</div>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={e=>setPassword(e.target.value)}
              placeholder="At least 8 characters"
              style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1.5px solid #2e5a56",background:"#1a3532",color:"#f8f4ee",fontSize:14,fontFamily:T.fontUI,outline:"none",boxSizing:"border-box"}}
            />
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"#6ea89f",textTransform:"uppercase",letterSpacing:.5,marginBottom:5}}>Confirm Password</div>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e=>setConfirm(e.target.value)}
              placeholder="••••••••"
              style={{width:"100%",padding:"11px 14px",borderRadius:10,border:"1.5px solid #2e5a56",background:"#1a3532",color:"#f8f4ee",fontSize:14,fontFamily:T.fontUI,outline:"none",boxSizing:"border-box"}}
            />
          </div>
          {err && (
            <div style={{background:"#3d1a1a",border:"1px solid #f87171",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#fca5a5"}}>
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{background:T.teal,color:"#fff",border:"none",borderRadius:10,padding:"13px",fontWeight:700,fontSize:14,cursor:busy?"not-allowed":"pointer",fontFamily:T.fontUI,opacity:busy?.7:1,marginTop:4}}>
            {busy ? "Saving…" : "Set New Password"}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── BRAND LOGO ──────────────────────────────────────────────────────────────
const APP_LOGO = "/brand/logo-options/logo-01-refined.png";
const LOGO_B64 = "data:image/png;base64,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";

// ─── CSV EXPORT UTILITY ───────────────────────────────────────────────────────
function toCSV(rows, headers) {
  const escape = v => {
    const s = String(v ?? "").replace(/"/g, '""');
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
  };
  const lines = [headers.join(","), ...rows.map(r => headers.map(h => escape(r[h])).join(","))];
  return lines.join("\n");
}
function downloadCSV(filename, csv) {
  const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Public routes intentionally bypass the authenticated CRM shell.  The booking
// page writes through a narrowly-scoped Supabase RPC (see supabase-schema.sql),
// never directly to CRM tables from an anonymous browser session.
export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/book") {
    window.history.replaceState({}, "", `/inquiry${window.location.search}${window.location.hash}`);
    return <BookingPage />;
  }
  if (path === "/inquiry") return <BookingPage />;
  if (["/", "/about", "/services", "/contact", "/privacy", "/terms"].includes(path)) return <PublicSite path={path} />;
  if (path === "/crm") {
    window.history.replaceState({}, "", "/os/crm");
    return <CrmApp />;
  }
  if (path === "/os" || path.startsWith("/os/")) return <CrmApp />;
  return <PublicSite path="/" />;
}
